import { registrarEvento } from "../core/auditLog.js";
import { repararTildes } from "../core/tildes.js";
import { calcularRetencionesVenta, generarAsientoVenta , calcularAutorretencionRenta} from "../core/accounting.js";
import { fechaEstaCerrada, saveData, siguienteNumeroDoc, getParametrosFiscales } from "../core/cloudSync.js";
import { avisoProrrateo, congelarRegimenItems, desgloseIVA, renglonesIVA, etiquetaRegimen, regimenDeLinea } from "../core/iva.js";
import { C, DOC_INFO } from "../core/config.js";
import { state } from "../core/state.js";
import { supa } from "../core/supabaseClient.js";
import { btnStyle, correoSinTildes, diasPagoDe, escapeHTML, fmtCOP, fmtDate, genId, inputStyle, parseNumber, parsePct, problemaCorreo, today, todayCO } from "../core/utils.js";
import { render } from "../ui/app.js";
import { exportPDFFactura } from "../export/pdf.js";
import { textoPlano } from "../core/textoRico.js";
import { documentoAjeno, facturaConNumero, normNumeroFactura, partesNumeroFactura, proximoConsecutivoLibre } from "../core/facturaIdentidad.js";
import { MEDIOS_PAGO_DATAICO, etiquetaMedioPago, itemDataico, medioPagoDe } from "../core/dataicoPayload.js";

// Configuración de numeración de la facturación electrónica (por empresa,
// sincronizada en tenant_config vía CONFIG_CLOUD_KEYS). Las CREDENCIALES de
// Dataico NO viven aquí ni en el navegador: son secretos del servidor que usa
// la Edge Function dataico-proxy.
export function getDataicoConfig() {
  try {
    const raw = localStorage.getItem("erp:dataico_config");
    const cfg = raw ? JSON.parse(raw) : null;
    if (cfg && cfg.api_key) delete cfg.api_key; // higiene: nunca conservar llaves viejas en el navegador
    return cfg;
  } catch { return null; }
}

// Se FUSIONA con lo guardado. Antes reemplazaba el objeto entero, y como la
// emisión de una factura guarda solo lo suyo (prefijo, resolución, consecutivo,
// medio de pago), cada factura borraba el prefijo y el consecutivo de las notas
// crédito (`nc_prefijo`, `nc_proximo_numero`) que guarda la emisión de una NC.
export function saveDataicoConfig(config) {
  const { api_key: _omitida, ...limpia } = { ...(getDataicoConfig() || {}), ...(config || {}) };
  try { localStorage.setItem("erp:dataico_config", JSON.stringify(limpia)); } catch {}
}

// Los medios de pago que acepta Dataico, con su nombre en español, viven en
// core/dataicoPayload.js (MEDIOS_PAGO_DATAICO), junto al armado de los ítems.

// ─── Qué se va a facturar: el PLAN ───────────────────────────────────────────
// De una misma orden de producción el cliente puede exigir cuatro cosas:
//   1. una factura por cada entrega,
//   2. una sola factura por todo lo entregado hasta ahora,
//   3. facturar por anticipado una parte, sin entregar,
//   4. facturar por anticipado toda la orden.
// Las cuatro terminan en el mismo sitio —la vista previa y la emisión—, así que
// lo que cambia es solo el plan: qué líneas, con qué cantidades, cubriendo qué
// documentos. `state.facturando` guarda ese plan.
//
// Las cantidades siempre salen del documento que las respalda: de la remisión
// cuando ya se entregó (facturar la orden entera por una entrega parcial cobraba
// mercancía que seguía en bodega), y de la propia OP cuando se factura por
// anticipado.

const _nom = (x) => String(x || "").trim().toLowerCase();

// Junta líneas del mismo artículo sumando cantidades (varias entregas del mismo
// producto salen como una sola línea en la factura).
function unirLineas(lineas) {
  const out = [];
  lineas.forEach(l => {
    const cant = parseNumber(l.cantidad) || 0;
    if (cant <= 0) return;
    const ya = out.find(x => _nom(x.articulo) === _nom(l.articulo) &&
      (parseNumber(x.precio_unitario) || 0) === (parseNumber(l.precio_unitario) || 0));
    if (ya) { ya.cantidad = (parseNumber(ya.cantidad) || 0) + cant; ya.total = ya.cantidad * (parseNumber(ya.precio_unitario) || 0); }
    else out.push({ ...l, cantidad: cant, total: cant * (parseNumber(l.precio_unitario) || 0) });
  });
  return out;
}

function docOrigenDeRC(rc) {
  if (!rc || !rc._origen || !rc._origen.tipo) return null;
  const info = DOC_INFO[rc._origen.tipo];
  return info ? (state.data[info.key] || []).find(d => d.id === rc._origen.id) || null : null;
}

// Plan para una o varias remisiones ya entregadas.
export function planDesdeRemisiones(rcIds, permitirFacturadas) {
  const rcs = (rcIds || []).map(id => (state.data.remisiones || []).find(r => r.id === id)).filter(Boolean);
  if (!rcs.length) return null;
  // Una entrega ya facturada no puede volver a entrar en una factura: sería
  // cobrarle al cliente dos veces la misma mercancía.
  if (!permitirFacturadas) {
    const yaFacturada = rcs.find(rc => rc.estado === "facturada" ||
      (state.data.facturas || []).some(f => f.estado !== "anulada" &&
        [f._origen, ...(Array.isArray(f._origenes) ? f._origenes : [])]
          .filter(Boolean).some(o => o.tipo === "RC" && o.id === rc.id)));
    if (yaFacturada) return { error: `La entrega ${yaFacturada.numero} ya está facturada.` };
  }

  // Una factura es de UN cliente. Se tomaba el del primero de la lista, así que
  // agrupar entregas de clientes distintos le habría facturado a uno la
  // mercancía del otro — y sin nota crédito eso no se deshace.
  const clientes = [...new Set(rcs.map(r => r.cliente_id || ""))];
  if (clientes.length > 1) {
    const nombres = clientes.map(id =>
      ((state.data.clientes || []).find(c => c.id === id) || {}).nombre || "sin cliente");
    return { error: "Esas entregas son de clientes distintos (" + nombres.join(", ") +
      "). Una factura es de un solo cliente: hay que facturarlas aparte." };
  }

  let parcial = false;
  const lineas = [];
  const vacias = [];   // remisiones sin líneas: no hay qué facturar en ellas
  rcs.forEach(rc => {
    const origen = docOrigenDeRC(rc);
    const itemsRC = (rc.items || []).filter(it => (parseNumber(it.cantidad) || 0) > 0);
    const itemsOrigen = origen ? (origen.items || []) : [];
    const cubreTodo = itemsOrigen.length === itemsRC.length && itemsOrigen.every(oi => {
      const ri = itemsRC.find(x => _nom(x.articulo) === _nom(oi.articulo));
      return ri && (parseNumber(ri.cantidad) || 0) === (parseNumber(oi.cantidad) || 0);
    });
    if (origen && !cubreTodo) parcial = true;
    // Una remisión SIN líneas no dice qué entregó, así que no hay nada que
    // facturar. Antes se interpretaba como "cubre su origen completo" y se
    // facturaba la ORDEN ENTERA: un borrador vacío de la OP-0214 habría
    // facturado sus $10.906.112 y sus 200 unidades como entregadas, sin que la
    // mercancía hubiera salido de la bodega y sin mover el kardex.
    if (!itemsRC.length) { vacias.push(rc.numero); return; }
    itemsRC.forEach(it => {
      const linea = itemsOrigen.find(x => _nom(x.articulo) === _nom(it.articulo)) || {};
      lineas.push({
        ...linea,
        articulo: it.articulo || linea.articulo || "",
        descripcion: it.descripcion || linea.descripcion || "",
        cantidad: parseNumber(it.cantidad) || 0,
        precio_unitario: parseNumber(linea.precio_unitario) || parseNumber(it.precio_unitario) || 0
      });
    });
  });

  if (vacias.length) {
    return { error: `${vacias.join(", ")} no tiene líneas: no dice qué se entregó, así que no hay nada que facturar.\n\n` +
      `Ábrela, ponle lo que se entregó y guárdala; si no se usó, elimínala.` };
  }

  // Una sola remisión que cubre su origen completo conserva los totales
  // guardados del documento origen (así no se pierde un descuento de cabecera).
  const unaCompleta = rcs.length === 1 && !parcial && docOrigenDeRC(rcs[0]);
  const origen0 = unaCompleta ? docOrigenDeRC(rcs[0]) : null;

  return {
    clienteId: rcs[0].cliente_id,
    rcIds: rcs.map(r => r.id),
    opId: (rcs[0]._origen && rcs[0]._origen.tipo === "OP") ? rcs[0]._origen.id : null,
    items: unirLineas(lineas),
    totales: origen0 ? {
      subtotal: parseNumber(origen0.subtotal) || 0,
      iva: parseNumber(origen0.iva) || 0,
      total: parseNumber(origen0.total) || 0
    } : null,
    sinEntrega: false,
    parcial,
    refs: rcs.map(r => r.numero).join(" + "),
    fechaRef: rcs[0].fecha,
    notas: rcs.map(r => r.notas).filter(Boolean).join("\n") || "",
    numero_oc_cliente: rcs[0].numero_oc_cliente || (docOrigenDeRC(rcs[0]) || {}).numero_oc_cliente || ""
  };
}

// Plan para facturar cantidades de la OP sin haber entregado.
// `cantidades` es { articulo: cantidad } con lo que se va a facturar.
export function planDesdeOP(opId, cantidades) {
  const op = (state.data.pedidos || []).find(o => o.id === opId);
  if (!op) return null;
  const lineas = (op.items || []).map(it => {
    const pedida = parseNumber((cantidades || {})[it.articulo]) || 0;
    return pedida > 0 ? { ...it, cantidad: pedida, total: pedida * (parseNumber(it.precio_unitario) || 0) } : null;
  }).filter(Boolean);
  if (!lineas.length) return null;
  return {
    clienteId: op.cliente_id,
    rcIds: [],
    opId: op.id,
    items: unirLineas(lineas),
    totales: null,
    sinEntrega: true,
    parcial: lineas.length !== (op.items || []).length ||
      lineas.some(l => (parseNumber(l.cantidad) || 0) < (parseNumber((op.items || []).find(x => _nom(x.articulo) === _nom(l.articulo)) || {}).cantidad) || 0),
    refs: op.numero,
    fechaRef: op.fecha,
    notas: op.notas || "",
    numero_oc_cliente: op.numero_oc_cliente || ""
  };
}

// Deja la factura amarrada a lo que cubre y marca esas entregas como facturadas.
// Si se facturó sin entregar, anota lo que queda por costear: el costo de ventas
// se registra cuando la mercancía salga de bodega, no ahora.
function amarrarFacturaAlPlan(nuevaFactura, plan, marcaExtraRC) {
  const origenes = origenesDelPlan(plan);
  nuevaFactura._origen = origenes[0] || null;
  if (origenes.length > 1) nuevaFactura._origenes = origenes;
  if (plan.sinEntrega) {
    nuevaFactura._sinEntrega = true;
    nuevaFactura._costoPendiente = (nuevaFactura.items || [])
      .map(it => ({ articulo: it.articulo, cantidad: parseNumber(it.cantidad) || 0 }))
      .filter(x => x.cantidad > 0);
  }
  const ids = new Set(plan.rcIds || []);
  if (ids.size) {
    saveData("remisiones", (state.data.remisiones || []).map(r => ids.has(r.id)
      ? {
          ...r,
          estado: "facturada",
          _convertedTo: { tipo: "FACT", id: nuevaFactura.id, numero: nuevaFactura.numero, fecha: nuevaFactura.fecha },
          ...(marcaExtraRC ? marcaExtraRC(nuevaFactura) : {})
        }
      : r));
  }
}

// Cifras del plan. Con `totales` guardados se respetan; si no, se calculan de
// las líneas con la tarifa de IVA vigente.
export function datosFacturacion(plan) {
  plan = plan || state.facturando;
  if (!plan) return { subtotal: 0, iva: 0, total: 0, itemsConPrecio: [] };
  const itemsConPrecio = (plan.items || []).filter(it => (parseNumber(it.cantidad) || 0) > 0);
  if (plan.totales && (parseNumber(plan.totales.total) || 0) > 0) {
    return { ...plan.totales, itemsConPrecio, parcial: !!plan.parcial, sinEntrega: !!plan.sinEntrega };
  }
  const subtotal = itemsConPrecio.reduce((s, it) =>
    s + (parseNumber(it.cantidad) || 0) * (parseNumber(it.precio_unitario) || 0), 0);
  // Por línea: un bien excluido (art. 424) o exento (art. 477) no causa IVA, y
  // cobrárselo al cliente en la factura electrónica sería un error ante la DIAN.
  const desglose = desgloseIVA(itemsConPrecio.map(it => ({
    ...it, total: (parseNumber(it.cantidad) || 0) * (parseNumber(it.precio_unitario) || 0)
  })));
  const iva = desglose.iva;
  return { subtotal, iva, total: subtotal + iva, itemsConPrecio, desglose,
           parcial: !!plan.parcial, sinEntrega: !!plan.sinEntrega };
}

// Los documentos que la factura va a cubrir, para dejarlos anotados en ella.
export function origenesDelPlan(plan) {
  const out = [];
  (plan.rcIds || []).forEach(id => {
    const rc = (state.data.remisiones || []).find(r => r.id === id);
    if (rc) out.push({ tipo: "RC", id: rc.id, numero: rc.numero, fecha: rc.fecha });
  });
  if (!out.length && plan.opId) {
    const op = (state.data.pedidos || []).find(o => o.id === plan.opId);
    if (op) out.push({ tipo: "OP", id: op.id, numero: op.numero, fecha: op.fecha });
  }
  return out;
}

// La cantidad a facturar por anticipado: mientras se escribe solo se guarda y al
// salir del campo se repinta (los totales y los avisos dependen de ella).
export function handleFactOPCantInput(e) {
  if (!state.factOP) return;
  state.factOP.cant[e.target.dataset.factOpCant] = e.target.value;
  if (e.type === "change") renderFacturarOPModal();
}

// ─── Qué se va a facturar de esta orden ──────────────────────────────────────
// Una pantalla con las dos vías que existen de verdad: facturar entregas ya
// hechas (una, varias o todas) o facturar cantidades por anticipado. Todo se
// mide contra la orden, así que una cantidad ya facturada no vuelve a aparecer.
export function showFacturarOP(opId) {
  const op = (state.data.pedidos || []).find(o => o.id === opId);
  if (!op) return;
  if (!state.factOP || state.factOP.opId !== opId) {
    state.factOP = { opId, rcSel: {}, cant: {} };
  }
  // La selección no puede sobrevivir a lo que ya se facturó: sin esta limpieza,
  // volver a abrir el selector traía marcada una entrega ya facturada y
  // "marcar todas" la metía otra vez en la factura siguiente.
  const vivas = new Set(remisionesDeOP(opId).filter(x => !x.facturada).map(x => x.rc.id));
  Object.keys(state.factOP.rcSel).forEach(id => { if (!vivas.has(id)) delete state.factOP.rcSel[id]; });
  state.factOP.cant = {};
  renderFacturarOPModal();
}

export function renderFacturarOPModal() {
  const sel = state.factOP;
  if (!sel) return;
  const op = (state.data.pedidos || []).find(o => o.id === sel.opId);
  if (!op) return;
  const cliente = (state.data.clientes || []).find(c => c.id === op.cliente_id) || {};
  const balance = balanceFacturacionOP(op);
  const entregas = remisionesDeOP(op.id);
  const porFacturar = entregas.filter(e => !e.facturada);

  // ── Vía 1: entregas hechas y sin facturar ────────────────────────────────
  const valorRC = (rc) => {
    const plan = planDesdeRemisiones([rc.id], true);
    return plan && !plan.error ? datosFacturacion(plan).total : 0;
  };
  const seleccionadas = Object.keys(sel.rcSel || {}).filter(id => sel.rcSel[id]);
  const totalSel = seleccionadas.length
    ? (() => { const pl = planDesdeRemisiones(seleccionadas, true); return pl && !pl.error ? datosFacturacion(pl).total : 0; })()
    : 0;

  const filasEntregas = porFacturar.length
    ? porFacturar.map(({ rc }) => {
        const on = !!(sel.rcSel || {})[rc.id];
        const unidades = (rc.items || []).reduce((s, it) => s + (parseNumber(it.cantidad) || 0), 0);
        return `
        <label data-action="fact-op-toggle-rc" data-id="${rc.id}"
          style="display:flex;align-items:center;gap:10px;padding:8px 11px;border-radius:8px;cursor:pointer;
                 border:1.5px solid ${on ? C.accent : C.border};background:${on ? C.accent + "0d" : "white"};">
          <span style="font-size:14px;">${on ? "☑️" : "⬜"}</span>
          <span style="font-weight:700;color:${C.text};font-size:12.5px;min-width:86px;">${escapeHTML(rc.numero)}</span>
          <span style="font-size:11px;color:${C.muted};">${fmtDate(rc.fecha)} · ${unidades} und</span>
          <span style="margin-left:auto;font-family:ui-monospace,monospace;font-weight:700;font-size:12px;color:${C.text};">${fmtCOP(valorRC(rc))}</span>
        </label>`;
      }).join("")
    : `<div style="font-size:11.5px;color:${C.muted};padding:6px 2px;">No hay entregas pendientes de facturar.</div>`;

  // ── Vía 2: cantidades por anticipado ─────────────────────────────────────
  const filasCant = balance.map(b => {
    const escrita = (sel.cant || {})[b.articulo];
    const valor = escrita != null ? escrita : "";
    const cant = parseNumber(valor) || 0;
    const excedePendiente = cant > b.porFacturar;
    const excedeStock = cant > b.disponible;
    const color = (excedePendiente || excedeStock) ? C.red : C.text;
    return `
      <tr style="border-top:1px solid ${C.border};">
        <td style="padding:5px 6px;">
          <div style="font-weight:600;font-size:11.5px;color:${C.text};">${escapeHTML(b.articulo)}</div>
          <div style="font-size:10px;color:${C.muted};">${fmtCOP(b.precio_unitario)} c/u · disponible ${b.disponible}${b.comprometido > 0 ? ` <span style="color:${C.red};">(${b.comprometido} de ${b.stock} ya vendidos a otro cliente)</span>` : ""}</div>
        </td>
        <td style="padding:5px 6px;text-align:right;font-size:11.5px;">${b.pedido}</td>
        <td style="padding:5px 6px;text-align:right;font-size:11.5px;color:${C.muted};">${b.facturado}</td>
        <td style="padding:5px 6px;text-align:right;font-size:11.5px;font-weight:700;">${b.porFacturar}</td>
        <td style="padding:5px 6px;">
          <input data-fact-op-cant="${escapeHTML(b.articulo)}" type="number" inputmode="decimal" min="0" max="${b.porFacturar}"
            value="${escapeHTML(String(valor))}" placeholder="0"
            style="${inputStyle}font-size:11.5px;padding:4px 6px;text-align:right;color:${color};font-weight:700;"/>
        </td>
        <td style="padding:5px 6px;text-align:right;font-family:ui-monospace,monospace;font-size:11.5px;font-weight:700;">
          ${fmtCOP(cant * b.precio_unitario)}
        </td>
      </tr>`;
  }).join("");

  const totalAnticipado = balance.reduce((s, b) =>
    s + (parseNumber((sel.cant || {})[b.articulo]) || 0) * b.precio_unitario, 0);

  // Avisos de la vía anticipada: no facturar más de lo que falta ni más de lo
  // que hay en bodega (la mercancía tiene que existir para poder entregarla).
  const problemas = [];
  balance.forEach(b => {
    const cant = parseNumber((sel.cant || {})[b.articulo]) || 0;
    if (cant <= 0) return;
    if (cant > b.porFacturar) problemas.push(`${b.articulo}: ya solo quedan ${b.porFacturar} por facturar de esta orden.`);
    else if (cant > b.disponible) problemas.push(b.comprometido > 0
      ? `${b.articulo}: solo hay ${b.disponible} disponibles (${b.stock} en bodega, ${b.comprometido} ya facturados a otro cliente y pendientes de entregar).`
      : `${b.articulo}: solo hay ${b.disponible} en inventario y no se puede facturar mercancía que no existe.`);
  });
  const hayCantidades = totalAnticipado > 0 && !problemas.length;

  const totalOP = parseNumber(op.total) || 0;
  const yaFacturado = (state.data.facturas || [])
    .filter(f => facturaCubreOP(f, op.id))
    .reduce((s, f) => s + (parseNumber(f.total) || 0), 0);

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;z-index:1040;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:720px;max-width:100%;max-height:92vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.22);">
        <div style="padding:11px 14px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;background:linear-gradient(135deg,${DOC_INFO.OP.color}14,#f8fafc);">
          <div>
            <h3 style="margin:0;font-size:15.5px;font-weight:800;color:${C.text};">💵 Facturar ${escapeHTML(op.numero)}</h3>
            <div style="font-size:10.5px;color:${C.muted};margin-top:2px;">${escapeHTML(cliente.nombre || "")} · orden por ${fmtCOP(totalOP)}${yaFacturado > 0 ? ` · ya facturado ${fmtCOP(yaFacturado)}` : ""}</div>
          </div>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;padding:4px 8px;">✕</button>
        </div>
        <div style="padding:12px 14px;">

          <div style="border:1px solid ${C.border};border-radius:9px;padding:11px 13px;margin-bottom:12px;">
            <div style="font-size:10px;font-weight:800;color:${C.muted};letter-spacing:.4px;margin-bottom:3px;">1 · FACTURAR LO ENTREGADO</div>
            <div style="font-size:11px;color:${C.muted};margin-bottom:8px;">
              Marca una entrega para facturarla sola, o varias para agruparlas en una única factura.
            </div>
            <div style="display:grid;gap:6px;">${filasEntregas}</div>
            ${porFacturar.length > 1 ? `
              <button data-action="fact-op-todas" style="${btnStyle(null,"ghost",true)}margin-top:8px;font-size:10.5px;">
                ${seleccionadas.length === porFacturar.length ? "Quitar todas" : "Marcar todas"}
              </button>` : ""}
            ${seleccionadas.length ? `
              <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:10px;padding-top:9px;border-top:1px dashed ${C.border};flex-wrap:wrap;">
                <div style="font-size:11.5px;color:${C.text};">
                  ${seleccionadas.length} entrega${seleccionadas.length === 1 ? "" : "s"} · <strong>${fmtCOP(totalSel)}</strong>
                </div>
                <button data-action="fact-op-facturar-entregas" style="${btnStyle("#10b981")}">👁 Revisar y facturar</button>
              </div>` : ""}
          </div>

          <div style="border:1px solid ${C.border};border-radius:9px;padding:11px 13px;">
            <div style="font-size:10px;font-weight:800;color:${C.muted};letter-spacing:.4px;margin-bottom:3px;">2 · FACTURAR SIN ENTREGAR</div>
            <div style="font-size:11px;color:${C.muted};margin-bottom:8px;">
              Para cuando el cliente pide la factura por adelantado. Escribe cuánto facturar de cada referencia:
              puede ser una parte o toda la orden. El costo de ventas se registrará cuando la mercancía salga con su remisión.
            </div>
            <div style="overflow-x:auto;">
              <table style="width:100%;border-collapse:collapse;">
                <thead><tr>
                  <th style="text-align:left;font-size:9.5px;color:${C.muted};font-weight:700;padding:0 6px 4px;">Referencia</th>
                  <th style="text-align:right;font-size:9.5px;color:${C.muted};font-weight:700;padding:0 6px 4px;">Orden</th>
                  <th style="text-align:right;font-size:9.5px;color:${C.muted};font-weight:700;padding:0 6px 4px;">Facturado</th>
                  <th style="text-align:right;font-size:9.5px;color:${C.muted};font-weight:700;padding:0 6px 4px;">Por facturar</th>
                  <th style="text-align:right;font-size:9.5px;color:${C.muted};font-weight:700;padding:0 6px 4px;width:90px;">A facturar</th>
                  <th style="text-align:right;font-size:9.5px;color:${C.muted};font-weight:700;padding:0 6px 4px;">Valor</th>
                </tr></thead>
                <tbody>${filasCant}</tbody>
              </table>
            </div>
            <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin-top:9px;padding-top:9px;border-top:1px dashed ${C.border};flex-wrap:wrap;">
              <button data-action="fact-op-todo-pendiente" style="${btnStyle(null,"ghost",true)}font-size:10.5px;">Poner todo lo que falta</button>
              <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
                <span style="font-size:11.5px;color:${C.text};">Subtotal: <strong>${fmtCOP(totalAnticipado)}</strong></span>
                <button data-action="fact-op-facturar-cantidades" ${hayCantidades ? "" : "disabled"}
                  style="${btnStyle(hayCantidades ? "#10b981" : null, hayCantidades ? null : "secondary")}${hayCantidades ? "" : "opacity:.55;cursor:not-allowed;"}">👁 Revisar y facturar</button>
              </div>
            </div>
            ${problemas.length ? `
              <div style="margin-top:9px;background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:7px 10px;font-size:10.5px;color:#991b1b;">
                ${problemas.map(t => `⛔ ${escapeHTML(t)}`).join("<br/>")}
              </div>` : ""}
          </div>

        </div>
      </div>
    </div>
  `;
  render();
}

// Facturar UNA remisión (el camino de siempre, desde la lista de remisiones).
export function showFacturarDataico(rcId) {
  const plan = planDesdeRemisiones([rcId]);
  if (!plan) return;
  if (plan.error) { alert("⚠️ " + plan.error); return; }
  showFacturarPlan(plan);
}

// Facturar el plan que se haya armado: aquí ya da igual si viene de una entrega,
// de varias o de la propia orden sin entregar.
export function showFacturarPlan(plan) {
  state.facturando = plan;
  state.facturandoRcId = (plan.rcIds && plan.rcIds[0]) || null;   // compatibilidad
  const cliente = state.data.clientes.find(c => c.id === plan.clienteId) || {};
  const config = getDataicoConfig() || {};

  const { subtotal, iva, total } = datosFacturacion(plan);
  const titulo = plan.sinEntrega
    ? "A FACTURAR SIN ENTREGAR (desde la orden)"
    : (plan.rcIds || []).length > 1 ? "ENTREGAS A FACTURAR" : "REMISIÓN A FACTURAR";

  // Checklist fiscal del cliente: el NIT es obligatorio para la DIAN; el resto
  // mejora la factura pero no bloquea (la dirección es opcional para Dataico).
  const chk = (cond, okTxt, malTxt) => cond
    ? `<div style="font-size:11px;color:#047857;">✔ ${okTxt}</div>`
    : `<div style="font-size:11px;color:${C.red};font-weight:600;">✘ ${malTxt}</div>`;

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:600px;max-width:100%;max-height:92vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;background:#f8fafc;">
          <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">🇨🇴 Facturación electrónica — Dataico</h3>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">

          <div style="background:#d1fae5;border:1px solid #86efac;border-radius:6px;padding:9px;margin-bottom:10px;">
            <div style="font-weight:700;color:#065f46;font-size:12px;">✅ Conectado a Dataico — producción</div>
            <div style="font-size:10.5px;color:#047857;margin-top:2px;">La factura se transmite de verdad a la DIAN con CUFE oficial. Las credenciales viven seguras en el servidor.</div>
          </div>

          <div style="background:#f8fafc;border-radius:6px;padding:10px;margin-bottom:10px;">
            <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">${titulo}</div>
            <div style="font-size:16px;font-weight:700;color:${C.text};">${escapeHTML(plan.refs || "")}</div>
            <div style="font-size:12px;color:${C.muted};margin-top:4px;">${escapeHTML(cliente.nombre || "")} • NIT ${escapeHTML(cliente.nit || "—")}</div>
            <div style="font-size:12px;color:${C.muted};">${fmtDate(plan.fechaRef)}</div>
            ${plan.sinEntrega ? `<div style="margin-top:6px;font-size:10.5px;color:#78350f;background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;padding:5px 8px;">
              📦 La mercancía todavía no se ha entregado. El costo de ventas se registrará cuando salga con su remisión.
            </div>` : ""}
            ${total > 0 ? `
              <div style="margin-top:10px;padding-top:10px;border-top:1px solid ${C.border};">
                <div style="font-size:11.5px;color:${C.muted};">Subtotal: <strong>${fmtCOP(subtotal)}</strong> • IVA: <strong>${fmtCOP(iva)}</strong></div>
                <div style="font-size:16px;font-weight:800;color:${C.mathiz};margin-top:4px;">TOTAL: ${fmtCOP(total)}</div>
              </div>
            ` : `<div style="margin-top:8px;font-size:11px;color:${C.red};font-weight:600;">⚠️ Sin valores: no hay precios en el documento origen.</div>`}
          </div>

          <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:6px;padding:10px;margin-bottom:10px;">
            <div style="font-size:10.5px;color:${C.muted};font-weight:700;margin-bottom:6px;">NUMERACIÓN DIAN (la de tu resolución en Dataico)</div>
            <div style="display:grid;grid-template-columns:1fr 1.4fr 1fr;gap:8px;">
              <div>
                <label style="font-size:10px;color:${C.muted};font-weight:600;">Prefijo</label>
                <input id="dataico-prefijo" value="${escapeHTML(config.prefijo || "")}" placeholder="Ej: FE" style="${inputStyle}"/>
              </div>
              <div>
                <label style="font-size:10px;color:${C.muted};font-weight:600;">N° de resolución</label>
                <input id="dataico-resolucion" value="${escapeHTML(config.resolucion || "")}" placeholder="Ej: 18764095238xxx" style="${inputStyle}"/>
              </div>
              <div>
                <label style="font-size:10px;color:${C.muted};font-weight:600;">N° de esta factura</label>
                <input id="dataico-numero" type="number" min="1" value="${config.proximo_numero != null ? escapeHTML(String(config.proximo_numero)) : ""}" placeholder="Consecutivo" style="${inputStyle}"/>
              </div>
            </div>
            <div style="font-size:10px;color:${C.muted};margin-top:5px;">💡 Debe continuar el consecutivo real de tu resolución (lo ves en tu panel de Dataico). Después de la primera emisión, Aura lo lleva solo. Dataico rechaza números repetidos, así que no hay riesgo de duplicar.</div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:8px;">
              <div>
                <label style="font-size:10px;color:${C.muted};font-weight:600;">Medio de pago</label>
                ${(() => {
                  // Un selector con el nombre en español: antes era un campo de
                  // texto con el código ("MUTUAL_AGREEMENT") y nadie lo cambiaba,
                  // así que todas las facturas salieron con «acuerdo mutuo».
                  const actual = medioPagoDe(config);
                  const opciones = MEDIOS_PAGO_DATAICO.some(m => m.codigo === actual)
                    ? MEDIOS_PAGO_DATAICO
                    : [...MEDIOS_PAGO_DATAICO, { codigo: actual, etiqueta: actual }];
                  return `<select id="dataico-medio" style="${inputStyle}cursor:pointer;">${opciones.map(m =>
                    `<option value="${escapeHTML(m.codigo)}" ${m.codigo === actual ? "selected" : ""}>${escapeHTML(m.etiqueta)}</option>`).join("")}</select>`;
                })()}
              </div>
              <div style="display:flex;align-items:end;padding-bottom:6px;">
                <label style="font-size:11px;color:${C.text};display:flex;align-items:center;gap:6px;cursor:pointer;">
                  <input id="dataico-correo" type="checkbox" ${config.enviar_correo === false ? "" : "checked"} style="width:15px;height:15px;"/>
                  Dataico envía la factura al correo del cliente
                </label>
              </div>
            </div>
          </div>

          ${/* Texto libre que VIAJA con la factura: se guarda en el documento
                (columna `notas`), sale impreso en el PDF de AURA y va en el
                campo `notes` del payload de Dataico, que lo imprime en la
                factura electrónica que recibe el cliente. Se siembra con las
                notas de las remisiones que se están facturando. */ ""}
          <div style="background:#fffbeb;border:1px solid #fcd34d;border-radius:6px;padding:10px;margin-bottom:10px;">
            <label for="dataico-notas" style="display:block;font-weight:700;color:#78350f;font-size:11px;margin-bottom:5px;">
              📝 Observaciones para la factura
            </label>
            <textarea id="dataico-notas" rows="3" placeholder="Ej: Corresponde a la orden de compra 4500123456. Entregas RS-0219 y RS-0222."
              oninput="if(state.facturando) state.facturando.notas=this.value;"
              style="${inputStyle}font-family:inherit;resize:vertical;min-height:58px;">${escapeHTML(plan.notas || "")}</textarea>
            <div style="font-size:10px;color:#92400e;margin-top:4px;">
              Este texto sale impreso en la factura electrónica que emite Dataico y en el PDF de AURA.
            </div>
          </div>

          <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:6px;padding:9px;margin-bottom:10px;">
            <div style="font-weight:700;color:${C.text};font-size:11px;margin-bottom:5px;">📋 Datos fiscales del cliente</div>
            ${chk(cliente.nit, `NIT: ${escapeHTML(cliente.nit || "")}`, "Sin NIT — obligatorio para facturar. Complétalo en Maestros → Clientes.")}
            ${(() => {
              // El destinatario real es el buzón DIAN; el correo del contacto
              // comercial solo entra si no hay buzón. Mostrar siempre
              // cliente.email hacía creer que el buzón no se estaba usando.
              const dest = cliente.email_fe || cliente.email || "";
              const mal = problemaCorreo(dest);
              const etiqueta = cliente.email_fe ? "Correo (buzón DIAN)" : "Correo (contacto comercial)";
              if (dest && mal) {
                const sug = correoSinTildes(dest);
                return chk(false, "", `${etiqueta}: ${escapeHTML(dest)} — ${escapeHTML(mal)}. La factura saldrá como enviada pero no llegará` +
                  (sug && sug !== dest ? `. ¿Será ${escapeHTML(sug)}?` : "."));
              }
              return chk(dest, `${etiqueta}: ${escapeHTML(dest)}`, "Sin correo — la factura no le llegará por email al cliente.");
            })()}
            ${chk(cliente.direccion, `Dirección: ${escapeHTML(cliente.direccion || "")}`, "Sin dirección (opcional, se omite del XML).")}
            <div style="font-size:10.5px;color:${C.muted};margin-top:4px;">Tipo: ${cliente.tipo_persona === "natural" ? "Persona natural" : "Persona jurídica"} · ${cliente.responsable_iva ? "Responsable de IVA" : "No responsable de IVA"}</div>
          </div>

          ${/* Una venta puede estar facturada por fuera del ERP (en el portal de
               Dataico, antes de conectarlo). Registrarla con "Factura local"
               inventaba un consecutivo FL- nuevo y le ponía la fecha de hoy:
               quedaban dos números para la misma venta y en el mes equivocado.
               Aquí se registra la que YA existe, con su número y su fecha. */ ""}
          <div style="background:#f8fafc;border:1px solid #bfdbfe;border-radius:6px;padding:10px;margin-bottom:10px;">
            <div style="font-weight:700;color:#1e40af;font-size:11px;margin-bottom:6px;">🧾 ¿Ya la facturaste por fuera del ERP?</div>
            <div style="display:grid;grid-template-columns:1.3fr 1fr;gap:8px;">
              <div>
                <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Número de la factura</label>
                ${/* El número se escribe como lo lleva la resolución DIAN, sin
                      guion: las externas se venían registrando "MP-157" y el
                      consecutivo real es "MP157" — dos escrituras del mismo
                      número que ningún control detectaba. */ ""}
                <input id="fact-ext-numero" placeholder="${config.prefijo ? `Ej: ${escapeHTML(config.prefijo)}${config.proximo_numero != null ? escapeHTML(String(config.proximo_numero)) : "158"}` : "Ej: FE1234"}" style="${inputStyle}"/>
                ${config.prefijo ? `<div style="font-size:9.5px;color:${C.muted};margin-top:3px;">Tu serie DIAN es <b>${escapeHTML(config.prefijo)}</b> + el consecutivo, sin guion.</div>` : ""}
              </div>
              <div>
                <label style="font-size:10.5px;color:${C.muted};font-weight:600;">Fecha de emisión</label>
                <input id="fact-ext-fecha" type="date" value="${plan.fechaRef || today()}" style="${inputStyle}"/>
              </div>
            </div>
            ${/* El CUFE es la identidad fiscal del documento. Si la factura se
                  emitió en el portal de Dataico, ya está en la DIAN y TIENE
                  cufe: sin traerlo, la copia de AURA no queda amarrada al
                  documento electrónico, su PDF no lo imprime y —lo que de
                  verdad duele— no se le puede hacer una nota crédito
                  electrónica desde AURA, porque la NC viaja con el cufe de la
                  factura que reversa (ver notasCredito.js). */ ""}
            <div style="margin-top:8px;">
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;">CUFE <span style="font-weight:500;">— si se emitió en Dataico, cópialo de su panel</span></label>
              <input id="fact-ext-cufe" placeholder="Pégalo aquí para poder anularla con nota crédito desde AURA" autocomplete="off" style="${inputStyle}font-family:ui-monospace,monospace;font-size:11px;"/>
            </div>
            <div style="font-size:10px;color:${C.muted};margin-top:5px;">
              Registra en AURA una factura que ya se emitió en otro sistema: no se transmite nada a la DIAN,
              se guarda con SU número y SU fecha, y queda la venta, el IVA, la cartera y el costo.
              <b>Con el CUFE</b> queda además amarrada a su documento electrónico: el PDF lo imprime y se le
              puede hacer nota crédito desde aquí.
            </div>
            <button data-action="registrar-factura-externa" style="${btnStyle("#2B4A82",null,true)}margin-top:7px;">📥 Registrar factura ya emitida</button>
          </div>

          <div style="display:flex;gap:10px;justify-content:flex-end;padding-top:12px;border-top:1px solid ${C.border};">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cancelar</button>
            <button data-action="facturar-local" style="${btnStyle("#6b7280")}" title="Genera la factura internamente sin transmitir a la DIAN.">📄 Factura local</button>
            <button data-action="previsualizar-factura" style="${btnStyle("#10b981")}" title="Muestra la factura como va a quedar; desde ahí se emite a la DIAN o se corrige.">👁 Revisar y emitir</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

// Divide el NIT colombiano en identificación y dígito de verificación.
function partirNit(nit) {
  const limpio = String(nit || "").replace(/[.\s]/g, "");
  const m = limpio.match(/^(\d+)-(\d)$/);
  return m ? { id: m[1], dv: m[2] } : { id: limpio.replace(/\D/g, ""), dv: null };
}

function fechaDataico(iso) {
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

// ─── Lo que va a viajar a la DIAN, calculado aparte ─────────────────────────
// Emitir es irreversible (una factura solo se anula con nota crédito), así que
// el mismo armado sirve para MOSTRARLO antes y para transmitirlo después: lo
// que se ve en la vista previa es exactamente lo que se envía, sin recalcular.
export function armarEmisionDataico() {
  const prefijo = (document.getElementById("dataico-prefijo")?.value || "").trim();
  const resolucion = (document.getElementById("dataico-resolucion")?.value || "").trim();
  const numero = parseInt(document.getElementById("dataico-numero")?.value, 10);
  const medio = (document.getElementById("dataico-medio")?.value || medioPagoDe(getDataicoConfig())).trim();
  const enviarCorreo = !!document.getElementById("dataico-correo")?.checked;
  // Las observaciones se sincronizan al plan mientras se escriben; si el campo
  // sigue en pantalla, lo que tenga escrito manda.
  const _notasEl = document.getElementById("dataico-notas");
  if (_notasEl && state.facturando) state.facturando.notas = _notasEl.value;

  if (!prefijo || !resolucion) return { error: "Escribe el prefijo y el número de resolución DIAN (los de tu cuenta Dataico)." };
  if (!numero || numero < 1) return { error: "Escribe el número consecutivo de esta factura." };

  // Un consecutivo NO se repite. Dataico es idempotente por (prefijo, número):
  // emitir con un número ya usado no emite nada nuevo, devuelve el documento
  // que ya existía con ese número — y AURA guardaba su CUFE en la factura
  // equivocada (pasó con MP157: el documento de CABLECOL quedó amarrado a la
  // factura de CROWN). Se compara como lo hace la DIAN, sin guiones, para que
  // una externa registrada a mano como "MP-157" también choque.
  const yaUsado = facturaConNumero(`${prefijo}${numero}`, state.data.facturas);
  if (yaUsado) {
    const dueño = (state.data.clientes || []).find(c => c.id === yaUsado.cliente_id);
    const libre = proximoConsecutivoLibre(prefijo, numero, state.data.facturas);
    return { error: `El consecutivo ${prefijo}${numero} ya está usado por la factura ${yaUsado.numero}` +
      (dueño ? ` de ${dueño.nombre}` : "") + ` (${fmtDate(yaUsado.fecha)}, ${fmtCOP(yaUsado.total)}).\n\n` +
      `Emitir con un número usado no crea una factura nueva: Dataico devuelve la que ya existe. ` +
      `El siguiente libre es ${prefijo}${libre}.` };
  }

  const plan = state.facturando;
  if (!plan) return { error: "No hay nada seleccionado para facturar." };
  const cliente = state.data.clientes.find(c => c.id === plan.clienteId) || {};
  if (!cliente.nit) return { error: "El cliente no tiene NIT. Complétalo en Maestros → Clientes antes de facturar." };

  const { subtotal, iva, total, itemsConPrecio, parcial } = datosFacturacion(plan);
  if (!itemsConPrecio.length || total <= 0) return { error: "La factura no tiene ítems con precios. Revisa el documento origen." };

  // Fecha de emisión EN HORARIO DE COLOMBIA. La DIAN exige que la fecha de la
  // factura coincida con la de firma (hora local); usar UTC de noche cae al día
  // siguiente y rechaza con FAD09e.
  const fechaEmisionCO = todayCO();

  // Vencimiento según días de pago del cliente, contado desde la fecha CO
  const diasPago = diasPagoDe(cliente);
  const fechaVenc = new Date(fechaEmisionCO + "T12:00:00");
  fechaVenc.setDate(fechaVenc.getDate() + diasPago);
  const fechaVencIso = fechaVenc.toISOString().slice(0, 10);

  const nitCliente = partirNit(cliente.nit);
  const esNatural = cliente.tipo_persona === "natural";
  const tipoDoc = (cliente.tipo_documento || "NIT").toUpperCase();

  const customer = {
    party_identification_type: ["NIT", "CC", "CE", "TI", "PASAPORTE"].includes(tipoDoc) ? tipoDoc : "NIT",
    party_identification: nitCliente.id,
    party_type: esNatural ? "PERSONA_NATURAL" : "PERSONA_JURIDICA",
    tax_level_code: cliente.responsable_iva ? "COMUN" : "SIMPLIFICADO",
    regimen: (cliente.regimen_fiscal || "").toLowerCase() === "simple" ? "SIMPLE" : "ORDINARIO",
    company_name: esNatural ? "" : (cliente.nombre || ""),
    first_name: esNatural ? (cliente.nombre || "") : "",
    family_name: "",
    // La factura electrónica va al buzón DIAN del cliente, NO al contacto
    // comercial que sale en la cotización: ese correo suele ser de un asesor y
    // la factura nunca llega a quien la radica. Si no hay buzón, se usa el de
    // contacto para no dejarla sin destinatario (la previa lo advierte).
    email: cliente.email_fe || cliente.email || "",
    phone: cliente.telefono || ""
  };
  // Dirección: solo si hay código DANE (5 dígitos: departamento + municipio).
  // Sin él, Dataico acepta la factura sin bloque de dirección.
  const dane = String(cliente.ciudad_dian || "").replace(/\D/g, "");
  if (cliente.direccion && dane.length === 5) {
    customer.department = dane.slice(0, 2);
    customer.city = dane.slice(2);
    customer.address_line = cliente.direccion;
    customer.country_code = "CO";
  }

  const invoice = {
    number: String(numero),
    issue_date: fechaDataico(fechaEmisionCO),
    payment_date: fechaDataico(fechaVencIso),
    invoice_type_code: "FACTURA_VENTA",
    payment_means: medio,
    payment_means_type: diasPago === 0 ? "CONTADO" : "CREDITO",
    order_reference: plan.numero_oc_cliente || "",
    numbering: { resolution_number: resolucion, prefix: prefijo, flexible: true },
    customer,
    // La tarifa va POR ÍTEM, no quemada en 19: así estaba, y le habría cobrado
    // IVA a un bien excluido o exento. Un exento y un excluido viajan los dos en
    // 0 —no hay otra tarifa para ellos— y se distinguen en AURA y en la
    // contabilidad. La nota crédito usa la MISMA puerta (core/dataicoPayload.js).
    items: itemsConPrecio.map(itemDataico),
    notes: plan.notas ? [textoPlano(plan.notas)] : []
  };

  // La factura tal como quedaría guardada en AURA: con esto la vista previa
  // puede mostrar el documento (y su PDF) sin haber emitido nada todavía.
  const facturaTentativa = {
    id: "preview",
    numero: `${prefijo}${numero}`,
    fecha: fechaEmisionCO,
    fecha_vencimiento: fechaVencIso,
    cliente_id: plan.clienteId,
    items: congelarRegimenItems(itemsConPrecio.map(it => ({
      articulo: it.articulo || "", descripcion: it.descripcion || "", referencia: it.referencia || "",
      cantidad: it.cantidad || 0, precio_unitario: it.precio_unitario || 0,
      total: (parseNumber(it.cantidad) || 0) * (parseNumber(it.precio_unitario) || 0)
    }))),
    subtotal, iva, total,
    estado: "emitida", estado_cobro: "por_cobrar",
    notas: plan.notas || "",
    condiciones_pago: diasPago === 0 ? "Contado" : `${diasPago} días`
  };

  return {
    prefijo, resolucion, numero, medio, enviarCorreo,
    plan, rc: { numero: plan.refs, notas: plan.notas, fecha: plan.fechaRef },
    cliente, subtotal, iva, total, itemsConPrecio,
    fechaEmisionCO, fechaVencIso, diasPago, customer, invoice, facturaTentativa,
    parcial: !!parcial,
    facturaNum: `${prefijo}${numero}`
  };
}

// ─── Vista previa: así va a quedar la factura ────────────────────────────────
// Antes solo había un confirm() con el número y el total. Emitir es un acto
// fiscal irreversible, así que aquí se ve el documento completo —lo que verá el
// cliente y lo que viaja a la DIAN— y desde ahí se decide: emitir o corregir.
export function previsualizarFacturaDataico() {
  const em = armarEmisionDataico();
  if (em.error) { alert("⚠️ " + em.error); return; }
  // Guardar lo tecleado: así "Corregir" devuelve el formulario tal como estaba.
  saveDataicoConfig({ prefijo: em.prefijo, resolucion: em.resolucion, proximo_numero: em.numero, payment_means: em.medio, medio_pago_elegido: true, enviar_correo: em.enviarCorreo });
  state._emisionPreview = em;
  renderPreviewFacturaModal();
}

// Montos de retención a partir de los porcentajes (que pueden venir como texto
// del campo). Retefuente e ICA van sobre el subtotal; el ReteIVA, sobre el IVA.
export function montosRetencion(pct, subtotal, iva) {
  const rf   = parsePct(pct && pct.rf)   || 0;
  const rica = parsePct(pct && pct.rica) || 0;
  const riva = parsePct(pct && pct.riva) || 0;
  const montoRF   = rf   > 0 ? Math.round(subtotal * rf   / 100) : 0;
  const montoRICA = rica > 0 ? Math.round(subtotal * rica / 100) : 0;
  const montoRIVA = riva > 0 && iva > 0 ? Math.round(iva * riva / 100) : 0;
  return { montoRF, montoRICA, montoRIVA, total: montoRF + montoRICA + montoRIVA };
}

// El % se escribe libre: mientras se teclea solo se guarda (re-renderizar
// mataría el foco) y al salir del campo se repintan los montos.
export function handleRetPctInput(e) {
  const em = state._emisionPreview;
  if (!em || !em.ret) return;
  em.ret[e.target.dataset.retPct] = e.target.value;
  if (e.type === "change") renderPreviewFacturaModal();
}

export function renderPreviewFacturaModal() {
  const em = state._emisionPreview;
  if (!em) return;
  const { cliente, rc, subtotal, iva, total, invoice, facturaTentativa, facturaNum, fechaEmisionCO, fechaVencIso, diasPago, medio, enviarCorreo } = em;

  // Retenciones que el cliente va a practicar: no cambian la factura ante la
  // DIAN (las practica el comprador), pero sí lo que se va a recibir y los
  // asientos. Se pueden aplicar aquí mismo: muchos clientes retienen aunque su
  // ficha esté en cero, y antes había que salirse a Maestros para arreglarlo.
  if (!em.ret) {
    em.ret = {
      rf:   parsePct(cliente.retefuente) || 0,
      rica: parsePct(cliente.reteica)    || 0,
      riva: parsePct(cliente.reteiva)    || 0,
      guardarEnFicha: false
    };
  }
  // Bienes o servicios: cambia la tarifa (2,5 % vs 4 %) Y la cuantía mínima
  // (27 UVT vs 4 UVT) — Dcto. 1625/2016, arts. 1.2.4.x. Se arranca por bienes.
  if (!em.concepto) em.concepto = "compras";
  const pct = em.ret;
  const ret = montosRetencion(pct, subtotal, iva);
  ret.total = ret.montoRF + ret.montoRICA + ret.montoRIVA;
  const neto = total - ret.total;

  // Las tarifas sugeridas salen de Parámetros Fiscales, no quemadas: cuando la
  // DIAN las cambia se actualiza esa pantalla y aquí se refleja solo.
  const pf = getParametrosFiscales();
  const uvt = parseNumber(pf.uvt) || 0;
  const esServicio = em.concepto === "servicios";
  const sugRF = parsePct(esServicio ? pf.retefuente_servicios_pct : pf.retefuente_compras_pct) || 0;
  const sugRIVA = parsePct(pf.reteiva_pct) || 0;
  // La tarifa de ICA se guarda en por mil (‰) porque así la certifica el RIT.
  // ‰ → %: 11,04 ‰ es 1,104 %. Se redondea a tres decimales para que el campo
  // no muestre 1.1039999999999999 por el binario del punto flotante.
  const sugRICA = Math.round((parsePct(pf.ica_tarifa_mil) || 0) * 100) / 1000;
  const baseUVT = parseNumber(esServicio ? pf.retefuente_servicios_base_uvt : pf.retefuente_compras_base_uvt) || 0;
  const baseMinima = baseUVT * uvt;
  const bajoMinimo = baseMinima > 0 && subtotal < baseMinima;

  // Autorretención especial de renta: la declara y paga la EMPRESA, no el
  // cliente. No cambia la factura ni lo que se recibe; se muestra para que no
  // aparezca de sorpresa en la declaración mensual de retenciones.
  const autorret = calcularAutorretencionRenta(subtotal);

  // Un botón por retención: con un clic pone la tarifa habitual y deja el % a
  // la vista para cambiarlo (cada actividad y cada municipio tienen la suya).
  const botonRet = (clave, etiqueta, sugerida, base) => {
    const valor = pct[clave];
    // Activa también con el campo vacío: si la empresa todavía no configuró la
    // tarifa (p. ej. el ICA de su RIT), el botón igual abre el % para escribirlo
    // en vez de no hacer nada al pulsarlo.
    const activa = valor === "" || parsePct(valor) > 0;
    return `
      <div style="display:flex;align-items:center;gap:7px;padding:3px 0;">
        <button data-action="toggle-retencion" data-ret="${clave}" data-sugerida="${sugerida}"
          title="${activa ? "Quitar esta retención de la factura" : `Aplicar ${etiqueta} (${sugerida}% sobre ${base})`}"
          style="${btnStyle(activa ? "#047857" : null, activa ? null : "secondary", true)}min-width:112px;text-align:left;">
          ${activa ? "✔" : "➕"} ${etiqueta}
        </button>
        ${activa ? `
          <input data-ret-pct="${clave}" type="number" inputmode="decimal" step="0.001" min="0" max="100" value="${valor}"
            style="${inputStyle}width:78px;padding:3px 6px;font-size:11px;text-align:right;"/>
          <span style="font-size:10.5px;color:${C.muted};">% de ${base}</span>
        ` : sugerida > 0
          ? `<span style="font-size:10.5px;color:${C.muted};">sugerido ${sugerida}% de ${base}</span>`
          : `<span style="font-size:10.5px;color:${C.amber};">sin tarifa configurada — se escribe a mano</span>`}
      </div>`;
  };

  // Avisos: lo que bloquea va en rojo; lo que solo conviene saber, en ámbar.
  const avisos = [];
  const correoFE = invoice.customer.email;
  if (!correoFE) avisos.push({ tipo: "ambar", txt: "El cliente no tiene correo: la factura no le llegará por email. Agrégale el correo de facturación electrónica en Maestros → Clientes." });
  else if (!cliente.email_fe) avisos.push({ tipo: "ambar", txt: `${correoFE} es el correo del contacto comercial, no un buzón de facturación electrónica. Si el cliente tiene uno (facturacionelectronica@…), ponlo en su ficha para que la factura le llegue a quien la radica.` });
  // Un correo mal escrito no impide emitir ante la DIAN, pero el cliente nunca
  // recibe la factura y el panel dice "correo enviado": hay que verlo ANTES.
  const malCorreo = problemaCorreo(correoFE);
  if (malCorreo) {
    const sugerido = correoSinTildes(correoFE);
    avisos.push({ tipo: "rojo", txt:
      `El correo ${correoFE} ${malCorreo}. La factura se emitirá igual ante la DIAN, pero ese buzón ` +
      `casi seguro rebota y el cliente no la recibirá` +
      (sugerido && sugerido !== correoFE ? `. ¿Será ${sugerido}? Corrígelo en Maestros → Clientes.` : ". Corrígelo en Maestros → Clientes.") });
  }
  if (correoFE && !enviarCorreo) avisos.push({ tipo: "ambar", txt: `Dataico NO enviará el correo a ${correoFE} (la casilla está desmarcada).` });
  if (!invoice.customer.address_line) avisos.push({ tipo: "ambar", txt: "Sin dirección con código DANE: la factura viaja sin bloque de dirección (la DIAN lo acepta)." });
  if (fechaEstaCerrada(fechaEmisionCO)) avisos.push({ tipo: "ambar", txt: "El mes de la fecha de emisión ya está cerrado en contabilidad: el asiento entrará al mes abierto conservando esta fecha como fecha del documento." });
  if (em.parcial) {
    avisos.push({ tipo: "ambar", txt: `Entrega parcial: la factura cobra solo lo que salió en ${rc.numero}, no la orden completa. Lo que quede pendiente se factura con su propia remisión.` });
  }
  if (bajoMinimo && ret.total > 0) {
    avisos.push({ tipo: "ambar", txt: `La base (${fmtCOP(subtotal)}) no llega a la cuantía mínima de ${baseUVT} UVT (${fmtCOP(baseMinima)}): por norma el cliente NO debería practicar retención en esta factura. Revisa si de verdad la va a retener.` });
  }
  if ((state.data.facturas || []).some(f => (f.numero || "").trim().toLowerCase() === facturaNum.toLowerCase())) {
    avisos.push({ tipo: "rojo", txt: `Ya hay una factura con el número ${facturaNum} en AURA. Revisa el consecutivo antes de emitir.` });
  }
  // Ítems sin costo en el catálogo: la venta se contabiliza igual, pero el
  // costo de la mercancía saldría en cero y el margen quedaría inflado.
  const sinCosto = invoice.items
    .map(x => x.sku)
    .filter(nom => {
      const prod = (state.data.productos || []).find(pr => pr.nombre === nom);
      return !prod || !(parseNumber(prod.costo) > 0);
    });
  if (sinCosto.length) avisos.push({ tipo: "ambar", txt: "Sin costo en el catálogo: " + sinCosto.join(", ") + ". La venta queda bien, pero el costo de la mercancía se descargaría en cero." });

  // Bienes que no causan IVA en la factura: se dice ANTES de emitir, porque es
  // irreversible y porque cambia lo que contabilidad tiene que hacer después.
  const noGravadas = facturaTentativa.items.filter(it => regimenDeLinea(it).tipo !== "gravado");
  if (noGravadas.length) {
    const detalle = noGravadas.map(it => `${it.articulo} (${etiquetaRegimen(regimenDeLinea(it))})`).join(", ");
    avisos.push({ tipo: "ambar", txt: "Esta factura lleva bienes que NO causan IVA: " + detalle +
      ". Se facturan en cero y así viajan a la DIAN. Verifica que la clasificación del producto sea la correcta: " +
      "un bien gravado facturado sin IVA es un impuesto que la empresa tendría que responder." });
  }
  const prorrateo = avisoProrrateo(facturaTentativa.items);
  if (prorrateo) avisos.push({ tipo: "ambar", txt: prorrateo });

  const filaDato = (etiqueta, valor) => `
    <div style="display:flex;justify-content:space-between;gap:10px;padding:3px 0;font-size:11.5px;">
      <span style="color:${C.muted};">${etiqueta}</span>
      <span style="color:${C.text};font-weight:600;text-align:right;">${valor}</span>
    </div>`;

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.5);display:flex;align-items:center;justify-content:center;z-index:1050;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:760px;max-width:100%;max-height:92vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.22);">
        <div style="padding:11px 14px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;background:linear-gradient(135deg,#eff6ff,#f8fafc);">
          <div>
            <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:800;">👁 Así va a quedar la factura</h3>
            <div style="font-size:10.5px;color:${C.muted};margin-top:2px;">Todavía no se ha enviado nada a la DIAN. Revisa y decide.</div>
          </div>
          <button data-action="close-modal" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;padding:4px 8px;">✕</button>
        </div>
        <div style="padding:12px 14px;">

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
            <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:8px;padding:9px 11px;">
              <div style="font-size:10px;font-weight:800;color:${C.muted};letter-spacing:.4px;margin-bottom:5px;">LA FACTURA</div>
              <div style="font-size:18px;font-weight:800;color:${C.mathiz};">${escapeHTML(facturaNum)}</div>
              ${filaDato("Emisión", fmtDate(fechaEmisionCO))}
              ${filaDato("Vence", `${fmtDate(fechaVencIso)} <span style="color:${C.muted};font-weight:400;">(${diasPago} días)</span>`)}
              ${filaDato("Resolución DIAN", escapeHTML(em.resolucion))}
              ${filaDato("Medio de pago", `${escapeHTML(etiquetaMedioPago(medio))} · ${diasPago === 0 ? "Contado" : "Crédito"}`)}
              ${filaDato("Desde la remisión", escapeHTML(rc.numero))}
            </div>
            <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:8px;padding:9px 11px;">
              <div style="font-size:10px;font-weight:800;color:${C.muted};letter-spacing:.4px;margin-bottom:5px;">A NOMBRE DE (datos que viajan)</div>
              <div style="font-size:13px;font-weight:800;color:${C.text};">${escapeHTML(cliente.nombre || "")}</div>
              ${filaDato("Identificación", `${escapeHTML(invoice.customer.party_identification_type)} ${escapeHTML(invoice.customer.party_identification)}`)}
              ${filaDato("Tipo", invoice.customer.party_type === "PERSONA_NATURAL" ? "Persona natural" : "Persona jurídica")}
              ${filaDato("IVA / régimen", `${invoice.customer.tax_level_code === "COMUN" ? "Responsable de IVA" : "No responsable"} · ${escapeHTML(invoice.customer.regimen)}`)}
              ${filaDato(cliente.email_fe ? "Correo (facturación electrónica)" : "Correo (contacto)",
                invoice.customer.email ? escapeHTML(invoice.customer.email) : `<span style="color:${C.amber};">— sin correo —</span>`)}
              ${filaDato("Dirección", invoice.customer.address_line ? escapeHTML(invoice.customer.address_line) : `<span style="color:${C.muted};">no viaja (sin código DANE)</span>`)}
            </div>
          </div>

          <div style="border:1px solid ${C.border};border-radius:8px;overflow:hidden;margin-bottom:10px;">
            <table style="width:100%;border-collapse:collapse;font-size:11.5px;">
              <thead>
                <tr style="background:#f1f5f9;">
                  <th style="padding:7px 9px;text-align:left;font-size:10px;font-weight:800;color:${C.muted};">REF (así lo verá el cliente)</th>
                  <th style="padding:7px 9px;text-align:left;font-size:10px;font-weight:800;color:${C.muted};">Descripción</th>
                  <th style="padding:7px 9px;text-align:right;font-size:10px;font-weight:800;color:${C.muted};">Cant.</th>
                  <th style="padding:7px 9px;text-align:right;font-size:10px;font-weight:800;color:${C.muted};">Precio</th>
                  <th style="padding:7px 9px;text-align:right;font-size:10px;font-weight:800;color:${C.muted};">IVA</th>
                  <th style="padding:7px 9px;text-align:right;font-size:10px;font-weight:800;color:${C.muted};">Total</th>
                </tr>
              </thead>
              <tbody>
                ${invoice.items.map(it => `
                  <tr style="border-top:1px solid ${C.border};">
                    <td style="padding:6px 9px;font-weight:700;color:${C.text};">${escapeHTML(it.sku)}</td>
                    <td style="padding:6px 9px;color:${C.muted};">${escapeHTML(it.description)}</td>
                    <td style="padding:6px 9px;text-align:right;">${it.quantity}</td>
                    <td style="padding:6px 9px;text-align:right;">${fmtCOP(it.price)}</td>
                    <td style="padding:6px 9px;text-align:right;color:${C.muted};">${it.taxes[0].tax_rate}%</td>
                    <td style="padding:6px 9px;text-align:right;font-weight:700;">${fmtCOP(it.quantity * it.price)}</td>
                  </tr>`).join("")}
              </tbody>
            </table>
          </div>

          <div style="display:flex;justify-content:flex-end;margin-bottom:10px;">
            <div style="min-width:280px;">
              ${filaDato("Subtotal", fmtCOP(subtotal))}
              ${renglonesIVA(facturaTentativa.items, iva).map(r => filaDato(r.lbl, fmtCOP(r.valor))).join("")}
              <div style="display:flex;justify-content:space-between;padding:7px 0 4px;border-top:2px solid ${C.mathiz};margin-top:4px;font-weight:800;font-size:14px;color:${C.mathiz};">
                <span>TOTAL FACTURA</span><span>${fmtCOP(total)}</span>
              </div>
              <div style="margin-top:6px;padding-top:6px;border-top:1px dashed ${C.border};">
                <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:4px;flex-wrap:wrap;">
                  <div style="font-size:10px;font-weight:800;color:${C.muted};">RETENCIONES QUE PRACTICA EL CLIENTE</div>
                  <div style="display:flex;gap:3px;">
                    ${[["compras","Bienes"],["servicios","Servicios"]].map(([v,l]) => `
                      <button data-action="concepto-retencion" data-concepto="${v}"
                        title="${v === "compras"
                          ? `Compras generales: ${parsePct(pf.retefuente_compras_pct)||0}% desde ${parseNumber(pf.retefuente_compras_base_uvt)||0} UVT`
                          : `Servicios: ${parsePct(pf.retefuente_servicios_pct)||0}% desde ${parseNumber(pf.retefuente_servicios_base_uvt)||0} UVT`}"
                        style="${btnStyle(em.concepto === v ? C.blue : null, em.concepto === v ? null : "secondary", true)}font-size:10px;padding:2px 8px;">${l}</button>`).join("")}
                  </div>
                </div>
                ${botonRet("rf", "Retefuente", sugRF, "subtotal")}
                ${botonRet("rica", "ReteICA", sugRICA, "subtotal")}
                ${botonRet("riva", "ReteIVA", sugRIVA, "IVA")}
                ${ret.total > 0 ? `
                  <div style="margin-top:5px;padding-top:5px;border-top:1px dashed ${C.border};">
                    ${ret.montoRF > 0 ? filaDato("Retefuente", "-" + fmtCOP(ret.montoRF)) : ""}
                    ${ret.montoRICA > 0 ? filaDato("ReteICA", "-" + fmtCOP(ret.montoRICA)) : ""}
                    ${ret.montoRIVA > 0 ? filaDato("ReteIVA", "-" + fmtCOP(ret.montoRIVA)) : ""}
                    <div style="display:flex;justify-content:space-between;padding:5px 0 0;font-weight:800;font-size:12.5px;color:#047857;">
                      <span>Se recibirá</span><span>${fmtCOP(neto)}</span>
                    </div>
                    <label style="display:flex;align-items:center;gap:6px;font-size:10.5px;color:${C.text};margin-top:6px;cursor:pointer;">
                      <input data-action="ret-guardar-ficha" type="checkbox" ${pct.guardarEnFicha ? "checked" : ""} style="width:14px;height:14px;"/>
                      Guardar estos % en la ficha de ${escapeHTML(cliente.nombre || "el cliente")}
                    </label>
                  </div>` : `
                  <div style="font-size:10px;color:${C.muted};margin-top:3px;">
                    Este cliente no retiene nada hoy. La factura ante la DIAN no cambia: las retenciones solo afectan lo que se recibe y la contabilidad.
                  </div>`}
              </div>
              ${autorret.monto > 0 ? `
                <div style="margin-top:8px;padding-top:6px;border-top:1px dashed ${C.border};font-size:10.5px;color:${C.muted};">
                  <strong style="color:${C.text};">Autorretención de renta ${autorret.pct}%:</strong> ${fmtCOP(autorret.monto)}
                  <div style="margin-top:2px;">La paga y declara Mathiz, no el cliente. No cambia la factura ni lo que se recibe.</div>
                </div>` : ""}
            </div>
          </div>

          ${avisos.length ? `
          <div style="display:grid;gap:6px;margin-bottom:10px;">
            ${avisos.map(a => `
              <div style="background:${a.tipo === "rojo" ? "#fef2f2" : "#fffbeb"};border:1px solid ${a.tipo === "rojo" ? "#fca5a5" : "#fcd34d"};border-radius:6px;padding:7px 10px;font-size:11px;color:${a.tipo === "rojo" ? "#991b1b" : "#78350f"};">
                ${a.tipo === "rojo" ? "⛔" : "⚠️"} ${escapeHTML(a.txt)}
              </div>`).join("")}
          </div>` : `
          <div style="background:#ecfdf5;border:1px solid #86efac;border-radius:6px;padding:7px 10px;font-size:11px;color:#065f46;margin-bottom:10px;">
            ✔ Todo en orden: no hay nada pendiente por corregir.
          </div>`}

          <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;padding:8px 10px;font-size:10.5px;color:#78350f;margin-bottom:10px;">
            Al emitir, esto se transmite de verdad a la DIAN y no se puede deshacer: una factura emitida solo se anula con nota crédito.
          </div>

          <div style="display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;padding-top:11px;border-top:1px solid ${C.border};">
            <button data-action="cancelar-preview-factura" style="${btnStyle(null,"secondary")}">✏️ Corregir</button>
            <button data-action="ver-pdf-preview-factura" style="${btnStyle(null,"ghost")}" title="Ver el documento tal como lo recibirá el cliente">🖨 Ver el PDF</button>
            <button data-action="enviar-dataico" style="${btnStyle("#10b981")}">⚡ Emitir a la DIAN</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

// Un rechazo NO deja rastro en AURA ni en la DIAN (se reintenta con el mismo
// consecutivo), pero sí en la bitácora: queda a nombre del número intentado,
// así que la bitácora de la factura que al fin salga con ese número lo
// muestra antes de la emisión.
function rechazoEnBitacora(numero, cliente, total, motivos, estadoDian) {
  registrarEvento("facturas", numero, "rechazo_dian", { referencia: numero, detalle: {
    cliente: (cliente && cliente.nombre) || "", total,
    motivo: repararTildes((motivos || []).map(String).join(" | ")).slice(0, 1500),
    estado_dian: estadoDian || ""
  } });
}

// ─── Reenviar por correo una factura ya emitida ─────────────────────────────
// La factura ya está validada por la DIAN y su CUFE no cambia: esto repite
// ÚNICAMENTE el envío del correo (send_dian queda en false). Hace falta cuando
// el buzón del cliente estaba mal escrito y rebotó —el caso que nadie ve,
// porque Dataico responde "correo enviado" igual— o cuando el cliente
// simplemente pide que se la vuelvan a mandar.
// Los datos de la emisión viven en `_dataico` (la columna de la nube se llama
// `dataico`, pero la app usa el nombre con guion bajo — ver ENTITY_SCHEMA), y
// la factura local se marca con `_local`. Mirar `f.dataico`/`f.es_local` daba
// siempre undefined y el botón no aparecía nunca con datos reales.
export function puedeReenviarFactura(f) {
  return !!(f && f._dataico && f._dataico.uuid && f.estado !== "anulada" && !f._externa && !f._local);
}

export function showReenviarFactura(factId) {
  const f = (state.data.facturas || []).find(x => x.id === factId);
  if (!f) return;
  if (!puedeReenviarFactura(f)) {
    alert(f && f._externa
      ? `${f.numero} se emitió fuera del ERP: el reenvío lo hace quien la emitió.`
      : "Esta factura no se transmitió por Dataico, así que no hay correo que reenviar.");
    return;
  }
  const cliente = (state.data.clientes || []).find(c => c.id === f.cliente_id) || {};
  const sugerido = cliente.email_fe || cliente.email || "";
  const previos = f._reenvios || [];
  const enviadoA = (f._dataico && f._dataico.respuesta && f._dataico.respuesta.customer
    && f._dataico.respuesta.customer.email) || "";

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;z-index:1200;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:470px;max-width:100%;max-height:90vh;overflow-y:auto;box-shadow:0 20px 60px rgba(0,0,0,.3);">
        <div style="padding:14px 18px;border-bottom:1px solid ${C.border};background:#eff6ff;border-radius:14px 14px 0 0;">
          <h3 style="margin:0;font-size:15.5px;color:#1e40af;font-weight:800;">📧 Reenviar ${escapeHTML(f.numero)} por correo</h3>
          <div style="font-size:11px;color:#1e40af;margin-top:3px;">${escapeHTML(cliente.nombre || "")}</div>
        </div>
        <div style="padding:16px 18px;">
          <div style="font-size:11px;color:${C.muted};margin-bottom:12px;line-height:1.5;">
            La factura ya está emitida y aceptada por la DIAN: esto <strong>no</strong> la vuelve a transmitir
            ni cambia su CUFE. Solo repite el envío del correo.
          </div>
          ${enviadoA ? `<div style="font-size:10.5px;color:${C.muted};margin-bottom:10px;">Al emitirla se envió a <strong style="color:${C.text};">${escapeHTML(enviadoA)}</strong>.</div>` : ""}
          <label style="font-size:10px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Enviar a</label>
          <input id="reenvio-correo" type="email" value="${escapeHTML(sugerido)}"
            placeholder="facturacionelectronica@empresa.com" style="${inputStyle}"/>
          <div id="reenvio-aviso">${avisoReenvioHTML(sugerido)}</div>
          <div style="font-size:10px;color:${C.muted};margin-top:6px;">
            Viene del ${cliente.email_fe ? "buzón DIAN" : "correo de contacto"} de la ficha del cliente. Puedes cambiarlo solo para este envío.
          </div>
          ${previos.length ? `
          <div style="margin-top:12px;border-top:1px solid ${C.border};padding-top:10px;">
            <div style="font-size:10px;color:${C.muted};font-weight:700;margin-bottom:5px;">REENVÍOS ANTERIORES</div>
            ${previos.slice(-5).reverse().map(r => `<div style="font-size:10.5px;color:${C.muted};">${fmtDate(r.fecha)} · ${escapeHTML(r.correo)}</div>`).join("")}
          </div>` : ""}
          <div style="display:flex;gap:9px;justify-content:flex-end;margin-top:16px;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cancelar</button>
            <button data-action="confirmar-reenvio-factura" data-id="${f.id}" style="${btnStyle("#2563eb")}">📧 Reenviar</button>
          </div>
        </div>
      </div>
    </div>`;
  render();
}

// Mismo aviso que en la ficha del cliente: una tilde aquí y el correo no llega.
export function avisoReenvioHTML(valor) {
  const mal = problemaCorreo(valor);
  if (!mal) return "";
  const sug = correoSinTildes(valor);
  return `<div style="margin-top:6px;background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:7px 9px;font-size:10.5px;color:#991b1b;line-height:1.45;">
    ⛔ Este correo ${escapeHTML(mal)} y casi seguro rebotará${sug && sug !== String(valor || "").trim() ? `. ¿Será <strong>${escapeHTML(sug)}</strong>?` : "."}
  </div>`;
}

export async function confirmarReenvioFactura(factId) {
  const f = (state.data.facturas || []).find(x => x.id === factId);
  if (!f || !puedeReenviarFactura(f)) return;
  const correo = (document.getElementById("reenvio-correo")?.value || "").trim();
  if (!correo) { alert("Escribe el correo de destino."); return; }
  const mal = problemaCorreo(correo);
  if (mal) {
    const sug = correoSinTildes(correo);
    alert(`⚠️ El correo ${correo} ${mal}.\n\nNo se envió nada: esa dirección casi seguro rebota` +
          (sug && sug !== correo ? ` — ¿será ${sug}?` : ".") + "\n\nCorrígelo y vuelve a intentar.");
    return;
  }

  state.modal = `
    <div style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1200;">
      <div style="background:white;border-radius:14px;padding:30px 40px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="font-size:40px;margin-bottom:10px;">📧</div>
        <div style="font-weight:700;color:${C.text};">Reenviando ${escapeHTML(f.numero)}…</div>
        <div style="font-size:11px;color:${C.muted};margin-top:6px;">No cierres esta ventana.</div>
      </div>
    </div>`;
  render();

  let resp;
  try {
    const { data, error } = await supa.functions.invoke("dataico-proxy", {
      body: { action: "reenviar_correo", uuid: f._dataico.uuid, correo, tipo: "factura" }
    });
    if (error) throw new Error(error.message || "No se pudo contactar el servidor de facturación.");
    resp = data;
  } catch (err) {
    state.modal = null; render();
    alert("⚠️ No se pudo reenviar: " + (err.message || err) + "\n\nLa factura sigue emitida; solo falló el envío del correo.");
    return;
  }

  // El proxy devuelve siempre 200 y adentro el estado real de Dataico.
  const httpOk = resp?.status >= 200 && resp?.status < 300;
  if (resp?.error || !httpOk) {
    state.modal = null; render();
    const detalle = resp?.error || JSON.stringify(resp?.data || {}).slice(0, 300);
    alert(`⚠️ Dataico no pudo reenviar la factura:\n\n${detalle}\n\nLa factura sigue emitida y válida.`);
    return;
  }

  const reenvios = [...(f._reenvios || []), { fecha: today(), correo, usuario: state.usuarioNombre || "" }];
  saveData("facturas", (state.data.facturas || []).map(x => x.id === factId ? { ...f, _reenvios: reenvios } : x));
  registrarEvento("facturas", f.id, "reenviar_correo", { referencia: f.numero, detalle: { correo } });
  state.modal = null; render();
  alert(`✅ ${f.numero} reenviada a ${correo}.\n\nNo se volvió a transmitir a la DIAN: la factura y su CUFE son los mismos.`);
}

export async function enviarADataico() {
  // Lo normal es llegar desde la vista previa (ya revisada). Si se llama
  // directo, se arma aquí mismo y se pide la confirmación de toda la vida.
  const em = state._emisionPreview || armarEmisionDataico();
  if (em.error) { alert("⚠️ " + em.error); return; }
  const vieneDePreview = !!state._emisionPreview;
  state._emisionPreview = null;
  const { prefijo, resolucion, numero, medio, enviarCorreo, plan, cliente,
          subtotal, iva, total, itemsConPrecio, fechaEmisionCO, fechaVencIso,
          diasPago, invoice } = em;

  saveDataicoConfig({ prefijo, resolucion, proximo_numero: numero, payment_means: medio, medio_pago_elegido: true, enviar_correo: enviarCorreo });

  if (!vieneDePreview) {
    // Aviso de confirmación: esto es un acto fiscal real.
    const okConfirm = confirm(`⚡ Vas a emitir la factura electrónica ${prefijo}${numero} por ${fmtCOP(total)} a nombre de ${cliente.nombre}.\n\nSe transmite de verdad a la DIAN y no se puede deshacer (solo se anula con nota crédito). ¿Continuar?`);
    if (!okConfirm) return;
  }

  // Modal de espera
  state.modal = `
    <div style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;">
      <div style="background:white;border-radius:14px;padding:30px 40px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="font-size:40px;margin-bottom:10px;">📡</div>
        <div style="font-weight:700;color:${C.text};">Transmitiendo a la DIAN vía Dataico…</div>
        <div style="font-size:11px;color:${C.muted};margin-top:6px;">No cierres esta ventana.</div>
      </div>
    </div>`;
  render();

  let resp;
  try {
    const { data, error } = await supa.functions.invoke("dataico-proxy", {
      body: { action: "emitir", enviar_correo: enviarCorreo, invoice }
    });
    if (error) throw new Error(error.message || "No se pudo contactar el servidor de facturación.");
    resp = data;
  } catch (err) {
    mostrarErrorDataico(["No se pudo transmitir la factura: " + (err.message || err), "La factura NO fue emitida. Revisa tu conexión e intenta de nuevo."]);
    return;
  }

  const dataicoData = resp?.data || {};
  const httpOk = resp?.status >= 200 && resp?.status < 300;
  const inv = dataicoData.invoice || dataicoData;

  // 1) Errores de validación de Dataico (payload mal formado)
  const errores = Array.isArray(dataicoData.errors) ? dataicoData.errors : null;
  if (!httpOk || errores) {
    const lista = errores
      ? errores.map(e => (e.path ? `[${[].concat(e.path).join(".")}] ` : "") + (e.error || JSON.stringify(e)))
      : ["Dataico respondió HTTP " + resp?.status + ": " + JSON.stringify(dataicoData).slice(0, 400)];
    rechazoEnBitacora(`${prefijo}${numero}`, cliente, total, lista);
    mostrarErrorDataico(["La DIAN/Dataico rechazó la factura. NO fue emitida:", ...lista]);
    return;
  }

  // 2) Dataico responde 200 pero la DIAN puede RECHAZAR el documento. Ese estado
  //    viaja en dian_status; NO es una emisión válida. Solo las notificaciones
  //    (Notificación:) son informativas — un Rechazo real invalida la factura.
  const dianStatus = String(inv.dian_status || "").toUpperCase();
  const mensajes = Array.isArray(inv.dian_messages) ? inv.dian_messages.map(String) : [];
  // Solo los "Rechazo:" invalidan; las "Notificación:" son informativas.
  const soloRechazos = mensajes.filter(m => /rechazo/i.test(m));
  const aceptada = dianStatus === "DIAN_ACEPTADO" || dianStatus === "ACEPTADO";
  const rechazada = dianStatus === "DIAN_RECHAZADO" || dianStatus === "RECHAZADO" || (!aceptada && soloRechazos.length > 0);
  if (rechazada) {
    // El MOTIVO puede no venir en `dian_messages`: hubo un rechazo cuyos únicos
    // mensajes eran dos "Notificación: RUT01" —informativas, salen también en
    // facturas aceptadas—, así que la pantalla anunciaba un rechazo sin decir
    // por qué. Se busca el motivo en los demás campos de la respuesta y, si no
    // hay ninguno, se dice explícitamente en vez de dejar al usuario mirando un
    // aviso que no explica nada.
    const otros = [];
    ["dian_status_message", "status_message", "status_description", "legal_status",
     "legal_status_message", "dian_response", "error", "message", "description", "reason"]
      .forEach(k => {
        const v = inv[k] != null ? inv[k] : dataicoData[k];
        if (typeof v === "string" && v.trim()) otros.push(`${k}: ${v.trim()}`);
        else if (Array.isArray(v) && v.length) otros.push(`${k}: ${v.map(String).join(" · ")}`);
      });
    const sinMotivo = !soloRechazos.length && !otros.length;
    // La respuesta completa queda en la consola: sin ella un rechazo sin motivo
    // no se puede diagnosticar después.
    console.error("Dataico — respuesta del rechazo:", JSON.stringify(dataicoData));
    rechazoEnBitacora(`${prefijo}${numero}`, cliente, total,
      [...soloRechazos, ...otros, ...(sinMotivo ? ["Sin regla de rechazo: " + (mensajes.join(" | ") || "sin mensajes")] : [])],
      inv.dian_status);
    mostrarErrorDataico([
      "La DIAN RECHAZÓ la factura. NO tiene validez fiscal:",
      ...(soloRechazos.length ? soloRechazos.map(String) : []),
      ...otros,
      ...(sinMotivo
        ? [`La DIAN no devolvió ninguna regla de rechazo. Los mensajes que llegaron son informativos` +
           (mensajes.length ? `: ${mensajes.map(String).join(" | ")}` : " y no señalan un error") + ".",
           "Suele ser un rechazo transitorio de su servicio de validación: vuelve a emitir con el MISMO número en unos minutos. Si se repite, la respuesta completa quedó en la consola del navegador (F12) para revisarla con Dataico."]
        : []),
      ...(!sinMotivo && !soloRechazos.length && mensajes.length
        ? [`Mensajes informativos de la DIAN: ${mensajes.map(String).join(" | ")}`] : []),
      `(estado DIAN: ${inv.dian_status || "desconocido"}). Corrige y vuelve a emitir con el mismo número; Dataico permite reintentar un consecutivo rechazado.`
    ]);
    return;
  }

  // Emitida: extraer CUFE/UUID de la respuesta (la estructura puede variar)
  const cufe = inv.cufe || inv.uuid || dataicoData.cufe || dataicoData.uuid || "";

  // 3) ¿El documento que llegó es EL NUESTRO? El QR de la representación
  //    gráfica lleva el NIT del adquirente y el total del documento firmado. Si
  //    no coinciden con lo que se acabó de mandar, Dataico devolvió OTRA factura
  //    —es lo que hace cuando el consecutivo ya estaba usado— y su CUFE no se
  //    puede guardar como si fuera el de esta venta.
  const ajeno = documentoAjeno(inv.qrcode || dataicoData.qrcode, { nit: cliente.nit, total });
  if (ajeno) {
    console.error("Dataico — devolvió un documento que no es esta factura:", JSON.stringify(dataicoData));
    rechazoEnBitacora(`${prefijo}${numero}`, cliente, total,
      [`Dataico devolvió otro documento: ${ajeno.numero || "sin número"} por ${fmtCOP(ajeno.total)} del NIT ${ajeno.nit || "?"}`]);
    const libre = proximoConsecutivoLibre(prefijo, numero + 1, state.data.facturas);
    mostrarErrorDataico([
      "Dataico devolvió un documento que NO es esta factura. No se registró nada.",
      `Se mandó ${fmtCOP(total)} a nombre de ${cliente.nombre || ""} (NIT ${cliente.nit || "?"}) y volvió el documento ` +
        `${ajeno.numero || "sin número"} por ${fmtCOP(ajeno.total)} del NIT ${ajeno.nit || "?"}.`,
      `Suele significar que el consecutivo ${prefijo}${numero} ya estaba emitido: Dataico no crea una factura nueva con un número usado, devuelve la que ya existe.`,
      `Vuelve a emitir con el consecutivo ${prefijo}${libre} y revisa en el panel de Dataico qué documento tiene el ${prefijo}${numero}.`
    ]);
    return;
  }

  // El número lo manda la RESPUESTA cuando la trae: con numeración flexible
  // Dataico puede asignar otro, y el consecutivo del documento firmado es el
  // que vale. Ponerle siempre el contador local dejaba en AURA un número que la
  // DIAN no conoce.
  const numeroDevuelto = String(inv.number || (inv.qrcode || "").match(/NumFac=([^\n\r]*)/)?.[1] || "").trim();
  const facturaNum = numeroDevuelto && normNumeroFactura(numeroDevuelto) !== normNumeroFactura(`${prefijo}${numero}`)
    ? numeroDevuelto
    : `${prefijo}${numero}`;
  const fechaEmision = new Date().toISOString();

  const nuevaFactura = {
    id: genId(),
    numero: facturaNum,
    fecha: fechaEmisionCO,
    fecha_vencimiento: fechaVencIso,
    cliente_id: plan.clienteId,
    items: congelarRegimenItems(itemsConPrecio.map(it => ({
      articulo: it.articulo || "",
      descripcion: it.descripcion || "",
      referencia: it.referencia || "",
      cantidad: it.cantidad || 0,
      precio_unitario: it.precio_unitario || 0,
      total: (parseNumber(it.cantidad) || 0) * (parseNumber(it.precio_unitario) || 0)
    }))),
    subtotal, iva, total,
    estado: "emitida",
    estado_cobro: "por_cobrar",
    _dataico: {
      real: true, numero: facturaNum, cufe, ambiente: "PRODUCCION", proveedor: "Dataico",
      fecha: fechaEmision, resolucion, prefijo,
      dian_status: inv.dian_status || "", uuid: inv.uuid || "",
      pdf_url: inv.pdf_url || "", qrcode: inv.qrcode || "",
      respuesta: dataicoData
    },
    notas: plan.notas || "",
    condiciones_pago: diasPago === 0 ? "Contado" : `${diasPago} días`,
    // Bienes o servicios: define la cuantía mínima con la que se evalúan las
    // retenciones si alguna vez hay que recalcularlas desde la ficha.
    concepto_retencion: em.concepto || "compras"
  };
  amarrarFacturaAlPlan(nuevaFactura, plan, (f) => ({
    _factura: { proveedor: "Dataico", numero: f.numero, cufe, ambiente: "PRODUCCION", fecha: fechaEmision, facturaId: f.id }
  }));

  // Lo revisado en la previa manda sobre la ficha del cliente: si ahí se aplicó
  // (o se quitó) una retención, la factura y su asiento salen con eso.
  if (em.ret) {
    const m = montosRetencion(em.ret, subtotal, iva);
    if (m.total > 0) nuevaFactura.retenciones = { retefuente: m.montoRF, reteica: m.montoRICA, reteiva: m.montoRIVA };
    if (em.ret.guardarEnFicha) {
      saveData("clientes", (state.data.clientes || []).map(c => c.id === plan.clienteId
        ? { ...c, retefuente: parsePct(em.ret.rf) || 0, reteica: parsePct(em.ret.rica) || 0, reteiva: parsePct(em.ret.riva) || 0 }
        : c));
    }
  }

  saveData("facturas", [...(state.data.facturas || []), nuevaFactura]);
  registrarEvento("facturas", nuevaFactura.id, "emitir_dian", { referencia: facturaNum, detalle: {
    cliente: cliente.nombre || "", total, cufe, estado_dian: inv.dian_status || "",
    correo: enviarCorreo ? (cliente.email_fe || cliente.email || "") : "no se envió",
    origen: origenesDelPlan(plan).map(o => o.numero).filter(Boolean).join(", ")
  } });

  // El consecutivo avanza solo para la próxima, desde el número que quedó de
  // verdad en el documento (que con numeración flexible puede no ser el local)
  // y saltándose los que ya estén usados.
  const partesEmitida = partesNumeroFactura(facturaNum);
  const desdeProx = Math.max(numero + 1, partesEmitida ? partesEmitida.consecutivo + 1 : 0);
  saveDataicoConfig({ prefijo, resolucion, payment_means: medio, medio_pago_elegido: true, enviar_correo: enviarCorreo,
    proximo_numero: proximoConsecutivoLibre(prefijo, desdeProx, [...(state.data.facturas || []), nuevaFactura]) });

  // Asiento contable de venta (causación)
  try {
    const _retV = calcularRetencionesVenta(nuevaFactura);
    if (_retV.total > 0) nuevaFactura.retenciones = { retefuente:_retV.montoRF, reteica:_retV.montoRICA, reteiva:_retV.montoRIVA };
    const asientoVenta = await generarAsientoVenta(nuevaFactura);
    nuevaFactura._asientoVenta = asientoVenta.id;
    saveData("facturas", (state.data.facturas || []).map(f => f.id === nuevaFactura.id ? nuevaFactura : f));
  } catch (e) {
    console.error("❌ Error generando asiento de venta para factura Dataico:", e);
  }

  const cliente2 = cliente;
  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:540px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};background:#ecfdf5;">
          <h3 style="margin:0;font-size:16px;color:#065f46;font-weight:700;">✅ Factura electrónica emitida ante la DIAN</h3>
        </div>
        <div style="padding:11px;">
          <div style="text-align:center;margin-bottom:10px;">
            <div style="font-size:48px;margin-bottom:8px;">🇨🇴</div>
            <div style="font-size:17px;font-weight:800;color:#059669;">${escapeHTML(facturaNum)}</div>
            <div style="font-size:11px;color:#047857;margin-top:4px;font-weight:600;">Transmitida a la DIAN vía Dataico${(() => {
              // El destinatario real es el buzón de facturación electrónica, y solo
              // si no hay, el del contacto comercial (ver cómo se arma el customer).
              // Mostrar siempre cliente2.email hacía creer que el buzón DIAN no se
              // estaba usando, cuando sí se estaba usando.
              const dest = cliente2.email_fe || cliente2.email || "";
              if (!enviarCorreo || !dest) return "";
              return ` · enviada a ${escapeHTML(dest)}${cliente2.email_fe ? " (buzón DIAN)" : " (contacto comercial)"}`;
            })()}</div>
          </div>
          <div style="background:#f8fafc;border-radius:6px;padding:10px;font-size:11.5px;margin-bottom:9px;">
            <div style="color:${C.muted};">Cliente: <strong style="color:${C.text};">${escapeHTML(cliente2.nombre || "")}</strong></div>
            <div style="color:${C.muted};margin-top:4px;">Total: <strong style="color:#059669;font-size:14px;">${fmtCOP(total)}</strong></div>
            <div style="color:${C.muted};margin-top:4px;">Vence: <strong style="color:${C.text};">${fmtDate(fechaVencIso)}</strong></div>
            ${cufe ? `<div style="color:${C.muted};margin-top:6px;word-break:break-all;font-size:10px;">CUFE: <span style="color:${C.text};">${escapeHTML(cufe)}</span></div>` : `<div style="color:#92400e;margin-top:6px;font-size:10px;">Dataico aceptó la factura; el CUFE se puede consultar en tu panel de Dataico.</div>`}
          </div>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cerrar</button>
            <button data-action="ir-a-factura" data-id="${nuevaFactura.id}" style="${btnStyle("#059669")}">💰 Ver factura</button>
            <button data-action="print-factura" data-id="${nuevaFactura.id}" style="${btnStyle(C.mathiz)}">📄 Ver / Descargar PDF</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

function mostrarErrorDataico(lineas) {
  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:540px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};background:#fef2f2;">
          <h3 style="margin:0;font-size:16px;color:#991b1b;font-weight:700;">❌ La factura no se emitió</h3>
        </div>
        <div style="padding:11px;">
          ${lineas.map((l, i) => `<div style="font-size:${i === 0 ? "12.5px;font-weight:700" : "11.5px"};color:${i === 0 ? "#991b1b" : "#7f1d1d"};margin-bottom:6px;word-break:break-word;">${escapeHTML(repararTildes(l))}</div>`).join("")}
          <div style="background:#f8fafc;border-radius:6px;padding:9px;font-size:10.5px;color:${C.muted};margin-top:6px;">Corrige lo indicado y vuelve a intentar. Nada quedó registrado en Aura ni en la DIAN.</div>
          <div style="display:flex;justify-content:flex-end;margin-top:10px;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Entendido</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

export async function facturarLocal() {
  const plan = state.facturando;
  if (!plan) return;
  const cliente = state.data.clientes.find(c => c.id === plan.clienteId) || {};

  const { subtotal, iva, total, itemsConPrecio } = datosFacturacion(plan);

  // Número de factura local (atómico, del servidor)
  const existentes = state.data.facturas || [];
  let facturaNum;
  try {
    facturaNum = await siguienteNumeroDoc("FL");
  } catch (err) {
    alert("⚠️ " + err.message);
    return;
  }
  const fechaEmision = new Date().toISOString();

  // Vencimiento según días de pago del cliente
  const diasPago = diasPagoDe(cliente);
  const fechaVenc = new Date();
  fechaVenc.setDate(fechaVenc.getDate() + diasPago);

  const nuevaFactura = {
    id: genId(),
    numero: facturaNum,
    fecha: today(),
    fecha_vencimiento: fechaVenc.toISOString().slice(0, 10),
    cliente_id: plan.clienteId,
    items: congelarRegimenItems(itemsConPrecio.map(it => ({
      articulo: it.articulo || "",
      descripcion: it.descripcion || "",
      referencia: it.referencia || "",
      cantidad: it.cantidad || 0,
      precio_unitario: it.precio_unitario || 0,
      total: (parseNumber(it.cantidad) || 0) * (parseNumber(it.precio_unitario) || 0)
    }))),
    subtotal, iva, total,
    estado: "emitida",
    estado_cobro: "por_cobrar",
    _local: true,  // marca para distinguir de Dataico
    notas: plan.notas || "",
    condiciones_pago: diasPago === 0 ? "Contado" : `${diasPago} días`
  };
  amarrarFacturaAlPlan(nuevaFactura, plan);

  saveData("facturas", [...existentes, nuevaFactura]);

  // Asiento contable de venta (causación) — se marca _asientoVenta para evitar doble asiento
  try {
    const _retV = calcularRetencionesVenta(nuevaFactura);
    if (_retV.total > 0) nuevaFactura.retenciones = { retefuente:_retV.montoRF, reteica:_retV.montoRICA, reteiva:_retV.montoRIVA };
    const asientoVenta = await generarAsientoVenta(nuevaFactura);
    nuevaFactura._asientoVenta = asientoVenta.id;
    saveData("facturas", (state.data.facturas || []).map(f => f.id === nuevaFactura.id ? nuevaFactura : f));
  } catch (e) {
    console.error("❌ Error generando asiento de venta para factura local:", e);
  }

  state.modal = `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:520px;max-width:100%;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};background:#f8fafc;">
          <h3 style="margin:0;font-size:16px;color:#065f46;font-weight:700;">✅ Factura local generada</h3>
        </div>
        <div style="padding:11px;">
          <div style="text-align:center;margin-bottom:10px;">
            <div style="font-size:48px;margin-bottom:8px;">📄</div>
            <div style="font-size:17px;font-weight:800;color:#059669;">${facturaNum}</div>
            <div style="font-size:10.5px;color:${C.muted};margin-top:4px;background:#f1f5f9;border-radius:6px;padding:4px 10px;display:inline-block;">⚙️ Factura local · Sin transmisión a la DIAN</div>
          </div>
          <div style="background:#f8fafc;border-radius:6px;padding:10px;font-size:11.5px;margin-bottom:9px;">
            <div style="color:${C.muted};">Desde: <strong style="color:${C.text};">${escapeHTML(plan.refs || "")}</strong></div>
            <div style="color:${C.muted};margin-top:4px;">Cliente: <strong style="color:${C.text};">${escapeHTML(cliente.nombre || "")}</strong></div>
            <div style="color:${C.muted};margin-top:4px;">Total: <strong style="color:#059669;font-size:14px;">${fmtCOP(total)}</strong></div>
            <div style="color:${C.muted};margin-top:4px;">Vence: <strong style="color:${C.text};">${fmtDate(nuevaFactura.fecha_vencimiento)}</strong></div>
          </div>
          <div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;padding:10px;font-size:10.5px;color:#78350f;margin-bottom:9px;">
            ⚠️ Esta factura no tiene validez fiscal ante la DIAN. Para la factura electrónica oficial usa "⚡ Emitir factura electrónica".
          </div>
          <div style="display:flex;gap:10px;justify-content:flex-end;">
            <button data-action="close-modal" style="${btnStyle(null,"secondary")}">Cerrar</button>
            <button data-action="ir-a-factura" data-id="${nuevaFactura.id}" style="${btnStyle("#059669")}">💰 Ver factura</button>
            <button data-action="print-factura" data-id="${nuevaFactura.id}" style="${btnStyle(C.mathiz)}">📄 Ver / Descargar PDF</button>
          </div>
        </div>
      </div>
    </div>
  `;
  render();
}

// ─── Registrar en AURA una factura ya emitida en otro sistema ───────────────
// Caso real: ventas facturadas en el portal de Dataico antes de conectarlo al
// ERP. La venta existe y está en la DIAN, pero AURA no la tiene: sin ingreso,
// sin cartera y sin costo descargado. Esto la registra tal como se emitió —su
// número, su fecha— sin transmitir nada. Si la fecha cae en un mes contable ya
// cerrado, el asiento se registra en el mes abierto conservando la fecha real
// del documento (lo hace crearAsiento).
export async function registrarFacturaExterna() {
  const plan = state.facturando;
  if (!plan) return;
  const numero = (document.getElementById("fact-ext-numero") || {}).value || "";
  const fecha  = (document.getElementById("fact-ext-fecha")  || {}).value || "";
  const cufe   = ((document.getElementById("fact-ext-cufe") || {}).value || "").trim();
  if (!numero.trim()) { alert("⚠️ Escribe el número de la factura tal como se emitió."); return; }
  if (!fecha) { alert("⚠️ Indica la fecha de emisión de la factura."); return; }

  // El número se guarda en la FORMA DE LA SERIE. Las externas se venían
  // escribiendo con guion (MP-150, MP-152, MP-157) y la resolución no lo lleva
  // (MP157): así el mismo consecutivo quedaba escrito de dos maneras, ninguna
  // comparación lo detectaba y el de Dataico seguía libre para volver a usarlo.
  const cfgExt = getDataicoConfig() || {};
  const partesExt = partesNumeroFactura(numero);
  const mismaSerie = !!(partesExt && cfgExt.prefijo
    && partesExt.prefijo === normNumeroFactura(cfgExt.prefijo));
  const numeroFinal = mismaSerie ? `${cfgExt.prefijo}${partesExt.consecutivo}` : numero.trim();

  // Y se compara como lo hace la DIAN: "MP-157" y "MP157" son el mismo número.
  const yaExiste = facturaConNumero(numeroFinal, state.data.facturas);
  if (yaExiste) {
    const dueño = (state.data.clientes || []).find(c => c.id === yaExiste.cliente_id);
    alert(`⚠️ El número ${numeroFinal} ya está usado por la factura ${yaExiste.numero}` +
      (dueño ? ` de ${dueño.nombre}` : "") + ` (${fmtDate(yaExiste.fecha)}, ${fmtCOP(yaExiste.total)}).\n\n` +
      `Dos facturas no pueden compartir consecutivo: revisa en el panel de Dataico cuál es el número de esta.`);
    return;
  }

  const cliente = state.data.clientes.find(c => c.id === plan.clienteId) || {};
  const { subtotal, iva, total, itemsConPrecio } = datosFacturacion(plan);
  if (!(total > 0)) { alert("⚠️ No hay valores para facturar."); return; }

  const diasPago = diasPagoDe(cliente);
  const venc = new Date(fecha + "T12:00:00");
  venc.setDate(venc.getDate() + diasPago);

  const nuevaFactura = {
    id: genId(),
    numero: numeroFinal,
    fecha,
    fecha_vencimiento: venc.toISOString().slice(0, 10),
    cliente_id: plan.clienteId,
    items: congelarRegimenItems(itemsConPrecio.map(it => ({
      articulo: it.articulo || "", descripcion: it.descripcion || "", referencia: it.referencia || "",
      cantidad: it.cantidad || 0, precio_unitario: it.precio_unitario || 0,
      total: (parseNumber(it.cantidad) || 0) * (parseNumber(it.precio_unitario) || 0)
    }))),
    subtotal, iva, total,
    estado: "emitida",
    estado_cobro: "por_cobrar",
    _externa: true,   // emitida fuera del ERP: no se transmite ni se reenvía
    // Con CUFE la factura queda amarrada a su documento electrónico: es el
    // mismo que ya está en la DIAN. Así su PDF lo imprime y la nota crédito
    // electrónica —que viaja con el cufe de la factura que reversa— es posible.
    ...(cufe ? { _dataico: { real: true, proveedor: "Dataico", ambiente: "PRODUCCION",
                             numero: numero.trim(), cufe, fecha } } : {}),
    notas: plan.notas || "",
    condiciones_pago: diasPago === 0 ? "Contado" : `${diasPago} días`
  };
  amarrarFacturaAlPlan(nuevaFactura, plan);

  let avisoFecha = "";
  try {
    const _ret = calcularRetencionesVenta(nuevaFactura);
    if (_ret.total > 0) nuevaFactura.retenciones = { retefuente:_ret.montoRF, reteica:_ret.montoRICA, reteiva:_ret.montoRIVA };
    const asiento = await generarAsientoVenta(nuevaFactura);
    nuevaFactura._asientoVenta = asiento.id;
    if (asiento.fecha_documento) {
      avisoFecha = `\n\nEl mes de la factura ya estaba cerrado: el asiento quedó con fecha ${fmtDate(asiento.fecha)} conservando ${fmtDate(asiento.fecha_documento)} como fecha del documento.`;
    }
  } catch (err) {
    alert("⚠️ No se pudo contabilizar la factura: " + (err.message || err) + "\n\nNo se registró nada.");
    return;
  }

  saveData("facturas", [...(state.data.facturas || []), nuevaFactura]);

  // Si la factura registrada es de la MISMA serie de la resolución, consumió un
  // consecutivo: el de Dataico tiene que avanzar. Sin esto, la próxima emisión
  // desde AURA vuelve a proponer un número ya usado —y Dataico, idempotente por
  // (prefijo, número), devuelve el documento viejo en vez de emitir el nuevo.
  let avisoConsecutivo = "";
  if (mismaSerie) {
    const prox = parseInt(cfgExt.proximo_numero, 10) || 0;
    if (partesExt.consecutivo >= prox) {
      const siguiente = proximoConsecutivoLibre(cfgExt.prefijo, partesExt.consecutivo + 1, state.data.facturas || []);
      saveDataicoConfig({ ...cfgExt, proximo_numero: siguiente });
      avisoConsecutivo = `\n\nEl consecutivo de la facturación electrónica quedó en ${cfgExt.prefijo}${siguiente}: ${numeroFinal} ya está usado.`;
    }
  }

  state.modal = null;
  state.facturandoRcId = null;
  state.facturando = null;
  state.factOP = null;
  render();
  alert(`✅ Factura ${nuevaFactura.numero} registrada en AURA (emitida fuera del ERP, no se transmitió nada).\n\n` +
        `Cliente: ${cliente.nombre || ""}\nFecha: ${fmtDate(fecha)}\nTotal: ${fmtCOP(total)}\n` +
        `Quedó la venta, el IVA, la cartera y el costo de la mercancía.` +
        (cufe ? `\n\nCUFE guardado: queda amarrada a su documento electrónico y se le puede hacer nota crédito desde AURA.`
              : `\n\n⚠️ Sin CUFE. Si se emitió en Dataico, edítala y pégalo: sin él no se le puede hacer nota crédito electrónica desde AURA.`) +
        avisoConsecutivo + avisoFecha);
}

import { balanceFacturacionOP, facturaCubreOP, remisionesDeOP } from "./documentos.js";
import { registerActions } from "../ui/actions.js";

registerActions({
  "reenviar-factura": (d, el, e) => { showReenviarFactura(d.id); return; },
  "confirmar-reenvio-factura": async (d, el, e) => { await confirmarReenvioFactura(d.id); return; },
  "previsualizar-factura": (d, el, e) => {previsualizarFacturaDataico();
      return;},
  "fact-op-toggle-rc": (d, el, e) => {
      if (!state.factOP) return;
      state.factOP.rcSel[d.id] = !state.factOP.rcSel[d.id];
      renderFacturarOPModal();
      return;},
  "fact-op-todas": (d, el, e) => {
      if (!state.factOP) return;
      const pendientes = remisionesDeOP(state.factOP.opId).filter(x => !x.facturada).map(x => x.rc.id);
      const todas = pendientes.every(id => state.factOP.rcSel[id]);
      pendientes.forEach(id => { state.factOP.rcSel[id] = !todas; });
      renderFacturarOPModal();
      return;},
  "fact-op-todo-pendiente": (d, el, e) => {
      if (!state.factOP) return;
      const op = (state.data.pedidos || []).find(o => o.id === state.factOP.opId);
      balanceFacturacionOP(op).forEach(b => {
        // Nunca más de lo que hay en bodega: facturar sin mercancía dejaría una
        // entrega imposible de cumplir.
        state.factOP.cant[b.articulo] = Math.min(b.porFacturar, b.stock) || "";
      });
      renderFacturarOPModal();
      return;},
  "fact-op-facturar-entregas": (d, el, e) => {
      if (!state.factOP) return;
      const ids = Object.keys(state.factOP.rcSel).filter(id => state.factOP.rcSel[id]);
      const plan = planDesdeRemisiones(ids);
      if (!plan) { alert("⚠️ No se pudo armar la factura con esas entregas."); return; }
      if (plan.error) { alert("⚠️ " + plan.error); return; }
      showFacturarPlan(plan);
      return;},
  // ─── Facturar varias entregas juntas desde el panel de remisiones ────────
  // El panel de "remisiones sin facturar" es donde se ve el pendiente de
  // verdad; facturar de a una obligaba a una factura por entrega aunque el
  // cliente pidiera una sola.
  "rs-panel-toggle": (d, el, e) => {
      if (e && e.type === "input") return;   // una casilla dispara input y change
      if (!state._rsFactSel) state._rsFactSel = {};
      if (state._rsFactSel[d.id]) delete state._rsFactSel[d.id];
      else state._rsFactSel[d.id] = true;
      render();
      return;},
  "rs-panel-limpiar": (d, el, e) => {
      state._rsFactSel = {};
      render();
      return;},
  "rs-panel-facturar": (d, el, e) => {
      const ids = Object.keys(state._rsFactSel || {}).filter(id => state._rsFactSel[id]);
      if (!ids.length) return;
      const plan = planDesdeRemisiones(ids);
      if (!plan) { alert("⚠️ No se pudo armar la factura con esas entregas."); return; }
      if (plan.error) { alert("⚠️ " + plan.error); return; }
      state._rsFactSel = {};
      showFacturarPlan(plan);
      return;},
  "fact-op-facturar-cantidades": (d, el, e) => {
      if (!state.factOP) return;
      const plan = planDesdeOP(state.factOP.opId, state.factOP.cant);
      if (!plan) { alert("⚠️ Escribe al menos una cantidad para facturar."); return; }
      showFacturarPlan(plan);
      return;},
  "toggle-retencion": (d, el, e) => {
      const em = state._emisionPreview;
      if (!em || !em.ret) return;
      const clave = d.ret;
      const activa = em.ret[clave] === "" || (parsePct(em.ret[clave]) || 0) > 0;
      const sug = parsePct(d.sugerida) || 0;
      // Sin tarifa sugerida se activa con el campo vacío, listo para escribirla.
      em.ret[clave] = activa ? 0 : (sug > 0 ? sug : "");
      renderPreviewFacturaModal();
      return;},
  "concepto-retencion": (d, el, e) => {
      const em = state._emisionPreview;
      if (!em) return;
      em.concepto = d.concepto === "servicios" ? "servicios" : "compras";
      renderPreviewFacturaModal();
      return;},
  "ret-guardar-ficha": (d, el, e) => {
      const em = state._emisionPreview;
      if (!em || !em.ret) return;
      em.ret.guardarEnFicha = !!el.checked;
      return;},
  "cancelar-preview-factura": (d, el, e) => {
      // Volver al formulario con lo que ya estaba escrito (se guardó al pasar
      // a la vista previa), sin haber emitido nada.
      const plan = state.facturando;
      state._emisionPreview = null;
      if (plan) showFacturarPlan(plan); else { state.modal = null; render(); }
      return;},
  "ver-pdf-preview-factura": (d, el, e) => {
      const em = state._emisionPreview;
      if (!em) return;
      // El PDF de la vista previa lleva las retenciones que se marcaron AQUÍ
      // (no las de la ficha): es lo que va a quedar en la factura al emitir.
      const _m = montosRetencion(em.ret, em.subtotal, em.iva);
      exportPDFFactura(null, { ...em.facturaTentativa,
        retenciones: { retefuente: _m.montoRF, reteica: _m.montoRICA, reteiva: _m.montoRIVA } });
      return;},
  "enviar-dataico": async (d, el, e) => {await enviarADataico();
      return;},
  "registrar-factura-externa": async (d, el, e) => {await registrarFacturaExterna();
      return;},
});
